import './App.css'
import './styles/gamification-tokens.css'
import './gamification.css'
import './forYou.css'
import { Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import timeliningLogo from './assets/logo.png'
import { collection, deleteDoc, doc, getDoc, onSnapshot, query, serverTimestamp, setDoc, where } from 'firebase/firestore'
import type { DetailedViewMode, DetailedSelection } from './components/DetailedView/types'
import { elementIdToDetailedSelection, findSelectionForNodeId } from './components/DetailedView/diff'
import type { AiOperationalProposalMetadata } from './services/ai/proposals/operationalProposalAdapter'
import { buildGoalsFromTimelineElements } from './utils/elementsToGoals'
import { applyManageModeSwitch, computeMirrorForSwitch, selectionElementId } from './utils/appSelection'
import { viewRequiresRealWorkspace } from './utils/previewSurfaceGating'
import { getRecentWorkspaceIds, recordRecentWorkspaceId } from './services/workspaceSwitch/recentWorkspaces'
import { RightSideNav, type RightSideNavProps } from './components/RightSideNav'
import { RightSideNavMobile } from './components/RightSideNavMobile'
import { OverflowTip } from './components/Common/Hover/OverflowTip'
import {
  isBudgetEnabled as isBudgetEnabledForWorkspace,
  isQuotaEnabled as isQuotaEnabledForWorkspace,
  isTeachingEnabled as isTeachingEnabledForWorkspace,
  isTrainingEnabled as isTrainingEnabledForWorkspace,
  isConstructionEnabled as isConstructionEnabledForWorkspace,
  isHospitalEnabled as isHospitalEnabledForWorkspace,
  isSitesEnabled as isSitesEnabledForWorkspace,
  isDeliveryEnabled as isDeliveryEnabledForWorkspace,
  isShopsEnabled as isShopsEnabledForWorkspace,
  isStaysEnabled as isStaysEnabledForWorkspace,
  isJobsEnabled as isJobsEnabledForWorkspace,
  isClinicsEnabled as isClinicsEnabledForWorkspace,
  isPlantEnabled as isPlantEnabledForWorkspace,
  isFitnessEnabled as isFitnessEnabledForWorkspace,
  isTimesheetsTierEligible as isTimesheetsTierEligibleForWorkspace,
  canViewStock as canViewStockForWorkspace,
} from './services/featureRegistry'
import { RunningTimerBar } from './components/Timesheets/RunningTimerBar'
import { DashboardCanvas } from './components/DashboardCanvas'
import { DashboardContainer } from './components/DashboardContainer'
import { OrbitProvider } from './components/Common/Orbit/OrbitProvider'
import { OrbitGestureLayer } from './components/Common/Orbit/OrbitGestureLayer'
import { OrbitSkinProvider } from './components/Common/Orbit/OrbitSkinContext'
import { Button } from './components/Common/Button'
import { IconButton } from './components/Common/IconButton'
import { LeaveWorkspaceConfirmModal } from './components/Common/LeaveWorkspaceConfirmModal'
import { WorkspaceWelcome } from './components/Onboarding/WorkspaceWelcome'
import { leaveWorkspaceAndPurgeOutbox } from './services/memberService'
import { Pressable } from './components/Common/Pressable'
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'
import { XRayProvider } from './components/Common/XRay'
import { PendingInviteInbox } from './components/Invites/PendingInviteInbox'
import { getWrapperRole } from './utils/wrapperRole'
import type { Goal } from './types/timeline'
import type { TimelineDate } from './types/timeline'
import { useData } from './context/useData'
import { useAuth } from './context/useAuth'
import { getDurationInDays, getTimelineBounds } from './utils/timeUtils'
import { useNavigation } from './context/useNavigation'
import { useCanvasScale } from './context/useCanvasScale'
import { useTimeline } from './context/useTimeline'
import { useManageDraft } from './context/manageDraftContextCore'
import { useChecklistDetailPopup } from './context/useChecklistDetailPopup'
import { WORKSPACE_DEFAULT_CHECKLIST_ID, type TimelineElement } from './context/TimelineContext'
import { useWorkspace } from './context/useWorkspace'
import { useFamilyAccess } from './hooks/useFamilyAccess'
import { useLaunchPersonalWorkspace } from './hooks/useLaunchPersonalWorkspace'
import { useCredits } from './context/useCredits'
import type { WorkspaceKind, WorkspaceRole } from './context/WorkspaceContext'
import { parseNonHelperRole, roleCanPerform } from './utils/workspacePermissions'
import type { TimelineBoardKey } from './types/schema'
import { useCanvasPermissions } from './hooks/useCanvasPermissions'
import { firestoreDb } from './services/firebase'
import { useNotifications } from './context/useNotifications'
import { FloatingMenu } from './components/Common/FloatingMenu'
import { Loader } from './components/Common/Loader'
import { WorkspaceBrandChip } from './components/Common/WorkspaceBrandChip'
import type { PurchaseTabKey } from './components/Settings/PurchasePage'
import type { SettingsSection } from './components/Settings/settingsNavigationModel'
import type { WorkspaceSettingsSection } from './components/Admin/AdminDashboard'
import type { EducationHubProps, EducationSubTab } from './components/Education/EducationHub'
import { parseEduTabParam } from './components/Education/eduTabParam'
import type { ConstructionHubProps } from './components/Ops/ConstructionHub'
import type { HospitalHubProps } from './components/Ops/HospitalHub'
import type { SitesHubProps } from './components/Ops/SitesHub'
import type { DeliveryHubProps } from './components/Ops/DeliveryHub'
import type { ShopsHubProps } from './components/Ops/ShopsHub'
import type { StaysHubProps } from './components/Ops/StaysHub'
import type { JobsHubProps } from './components/Ops/JobsHub'
import type { ClinicsHubProps } from './components/Ops/ClinicsHub'
import type { PlantHubProps } from './components/Ops/PlantHub'
import type { FitnessHubProps } from './components/Ops/FitnessHub'
import { parseOpsTabParam, type OpsTab } from './components/Ops/opsTabParam'
import {
  OPS_SECTORS,
  OPS_SECTOR_KEYS,
  findOpsSectorByRouteSegment,
  findOpsSectorByView,
} from './types/ops'
import { useRaid } from './context/useRaid'
import { useAppLifecycle } from './hooks/useAppLifecycle'
import { usePersistedState } from './hooks/usePersistedState'
import { resolveProfileImageUrl } from './utils/profileImage'
import type { NotificationRecord } from './types/schema'
import type { MeetingRecord } from './types/meetings'
import { BOARD_CODE_BY_KEY, parseBoardCode } from './utils/publicIds'
import { AVAILABLE_VIEW_KEYS, COMING_SOON_VIEW_KEYS, type DashboardViewKey } from './types/schema'
import { createStripboardRecordDefaults } from './utils/stripboardDefaults'
import { useClientPreferences } from './context/useClientPreferences'
import { useChat } from './context/useChat'
import { useDisplayName } from './hooks/useDisplayName'
import {
  DASHBOARD_VIEW_MAP,
  type DashboardView,
} from './components/Common/mobileDashboardDockConstants'
import { isNoSignInPreviewActive } from './debug'
import { RaidCelebration } from './components/Social/RaidCelebration'
import { getConsoleParts, legacyTeamRedirect, toConsolePath, workspaceRootPath } from './utils/consoleRoutes'
import { lazyWithStaleChunkRetry } from './utils/lazyWithStaleChunkRetry'
import { selectRightRailNavigationGuard } from './utils/rightRailNavigationGuards'
import { isPersonalWorkspace, isTeamWorkspace } from './utils/workspaceKind'
import { ScreenGuideHost } from './components/ScreenGuide'
import { TourGuideContext } from './components/tourguide/tourGuideContextCore'
import { shouldMountDashboardContainer } from './utils/dashboardMountGate'
import { DebugModeIndicator } from './components/Debug/DebugModeIndicator'
import { ViewerBanner } from './components/Preview/ViewerBanner'
import { PreviewExitIntent } from './components/Preview/PreviewExitIntent'
import { PREVIEW_GRADUATION_EVENT } from './hooks/usePreviewGuard'
import { isSurfaceAllowed, type WorkspaceSurfaceKey } from './types/workspaceSurfaceMask'
import {
  type ManageWorkspaceMode,
} from './components/Manage/ManageWorkspaceHeaderControls'
import { ManageActionsBar } from './components/Manage/ManageActionsBar'
import { ManageTopChrome } from './components/Manage/ManageTopChrome'
import {
  renderBoardBreadcrumb,
  computeOpenChain,
  getChildElementsByParent,
} from './components/BoardExplorer'
import { ReviewHeadingContent } from './components/DetailedView/DetailedViewScreen'
import type { FocusExperiencePageProps } from './components/Focus/FocusExperiencePage'
import type { AiChatAttachment, AiChatSession } from './services/aiChatHistoryService'
import type { AiChatHandoffRequest, AiChatHandoffTarget } from './types/aiChatSource'
import { resolveDigestItem, resolveNotification, type DigestInnerItem, type NotificationRouterCtx, type NotificationRouterHandle } from './services/notificationRouter'
import { switchToWorkspaceForTarget, requestWorkspaceSwitch } from './services/workspaceSwitchService'
import { openWorkspaceTarget, parseWorkspaceDeepLink } from './services/workspaceSwitch/openWorkspaceTarget'
import { resolveSwitchLanding, safeWorkspaceLanding, buildUrlRestoreOnStay } from './services/workspaceSwitch/switchLanding'
import { useCreateWorkspaceOnboarding } from './components/Onboarding/useCreateWorkspaceOnboarding'
import { useIsMobile } from './components/Common/useIsMobile'
import { useCurrentPath } from './components/tourguide/useCurrentPath'
import { useWelcomeTourAutoStart } from './components/tourguide/useWelcomeTourAutoStart'
import { shouldShowWorkspaceWelcome } from './utils/workspaceWelcome'
import { useToast } from './context/useToast'
import { useNls } from './nls/nlsProvider'
import { useModuleRouteGuard } from './hooks/useModuleRouteGuard'
import { subscribePageVisibility, usePageVisibility } from './services/pageVisibilityService'
import { isWorkspaceOwner } from './utils/workspaceOwners'
import {
  type ModuleKey,
  MODULE_TOAST_DURATION_MS,
  switchedOffModulesFor,
} from './utils/workspaceModules'
import { MODULE_SETTING_TITLE_KEYS } from './components/Admin/WorkspaceModulesPanel'
import { consumeExploreDeepLink } from './services/eduDemo'
import { isPeriodKey, parseChecklistDeepLink } from './services/checklistDeepLinkService'

type DisplayRange = {
  startDate: TimelineDate | Date
  endDate: TimelineDate | Date
}

type ActiveLayoutView =
  | 'DASHBOARD'
  | 'CALENDAR'
  | 'EXPLORE'
  | 'USER_SETTINGS'
  | 'YOUR_PAGE'
  | 'PURCHASE'
  | 'INVITES'
  | 'LINK'
  | 'STOCK'
  | 'CLIENTS'
  | 'TIMESHEETS'
  | 'BUDGET'
  | 'QUOTA'
  | 'TEACHING'
  | 'TRAINING'
  | 'CONSTRUCTION'
  | 'HOSPITAL'
  | 'SITES'
  | 'DELIVERY'
  | 'SHOPS'
  | 'STAYS'
  | 'JOBS'
  | 'CLINICS'
  | 'PLANT'
  | 'FITNESS'
  | 'UPLOAD'
  | 'FORMS'
  | 'MEETINGS'
  | 'AUTOMATE'
  | 'DESIGN'
  | 'AI_MAGIC'
  | 'CHAT'
  | 'CHECKLISTS'
  | 'MANAGE'
  | 'RAID'
  | 'FOR_YOU'
  | 'FOCUS'
  | 'WORKSPACE_SETTINGS'
  | 'SUMMARY'
  | 'OVERVIEW'
  | 'ANALYSIS'
  | 'REPORTS'

type PendingInvite = {
  id: string
  email: string
  workspaceId: string
  role: WorkspaceRole
}

type IncomingJoinRequest = {
  id: string
  workspaceId: string
  applicantEmail: string
  applicantUid: string
}

type RestrictedWorkspaceRoute = {
  workspaceId: string
  slug: string
  name: string
  isPublic: boolean
}

const workspaceRoleLabel = (role: WorkspaceRole, isPreview?: boolean) => {
  if (isPreview) return 'Preview/Demo'
  if (role === 'READONLY') return 'Read-only'
  return role.charAt(0) + role.slice(1).toLowerCase()
}

const DesktopOnlyFallback = ({ title }: { title: string }) => (
  <section className="desktopOnlyFallback railPageCard" aria-label={title}>
    <div>
      <span className="settingsEyebrow">Desktop workspace</span>
      <h1>{title}</h1>
      <p>This surface is optimized for the desktop app. Use the desktop workspace for full controls.</p>
    </div>
  </section>
)

const AppSurfaceFallback = () => <div className="commandHubSuspense"><Loader /></div>

// Walk the AI-edited Goal tree alongside the baseline and emit one proposal
// per node whose id appears in aiChangedIds. Fields chosen match what
// BoardExplorer renders as editable inputs (title/description/dates/color) —
// keeping the diff surface aligned with what the user can actually Save.
// Checklist arrays are deliberately excluded: they live in a separate flat
// Firestore collection and need their own sync path, not a whole-array
// overwrite via manageDraft.
type AiProposalRecord = {
  id: string
  kind: 'goal' | 'task' | 'subtask'
  parentId: string | null
  originalSnapshot: unknown
  proposedFields: Record<string, unknown>
}
const collectAiEditProposedFields = (
  goal: Goal,
  baseline: Goal | null,
  changedIds: string[] | null,
): AiProposalRecord[] => {
  if (!changedIds || changedIds.length === 0) return []
  const changed = new Set(changedIds)
  const baselineIndex = new Map<string, unknown>()
  if (baseline) {
    baselineIndex.set(baseline.id, baseline)
    baseline.tasks?.forEach((task) => {
      baselineIndex.set(task.id, task)
      task.subTasks?.forEach((sub) => baselineIndex.set(sub.id, sub))
    })
  }
  const proposals: AiProposalRecord[] = []
  const pickFields = (node: { title?: string; description?: string; startDate?: unknown; endDate?: unknown; color?: string; baseColor?: string }): Record<string, unknown> => ({
    title: node.title ?? '',
    description: node.description ?? '',
    startDate: node.startDate ?? null,
    endDate: node.endDate ?? null,
    // TimelineElement stores color; Goal exposes it as baseColor.
    color: node.color ?? node.baseColor ?? undefined,
  })
  if (changed.has(goal.id)) {
    proposals.push({
      id: goal.id,
      kind: 'goal',
      parentId: null,
      originalSnapshot: baselineIndex.get(goal.id) ?? null,
      proposedFields: pickFields(goal),
    })
  }
  goal.tasks?.forEach((task) => {
    if (changed.has(task.id)) {
      proposals.push({
        id: task.id,
        kind: 'task',
        parentId: goal.id,
        originalSnapshot: baselineIndex.get(task.id) ?? null,
        proposedFields: pickFields(task),
      })
    }
    task.subTasks?.forEach((sub) => {
      if (changed.has(sub.id)) {
        proposals.push({
          id: sub.id,
          kind: 'subtask',
          parentId: task.id,
          originalSnapshot: baselineIndex.get(sub.id) ?? null,
          proposedFields: pickFields(sub),
        })
      }
    })
  })
  return proposals
}

const AiGeneratorPanel = lazyWithStaleChunkRetry('AiGeneratorPanel', () => import('./components/AiGeneratorPanel').then((module) => ({ default: module.AiGeneratorPanel })))
const DetailedViewScreen = lazyWithStaleChunkRetry('DetailedViewScreen', () => import('./components/DetailedView/DetailedViewScreen').then((module) => ({ default: module.DetailedViewScreen })))
const ManageWorkspacePage = lazyWithStaleChunkRetry('ManageWorkspacePage', () => import('./components/Manage/ManageWorkspacePage').then((module) => ({ default: module.ManageWorkspacePage })))
const AdminDashboard = lazyWithStaleChunkRetry('AdminDashboard', () => import('./components/Admin/AdminDashboard').then((module) => ({ default: module.AdminDashboard })))
const AuditReportView = lazyWithStaleChunkRetry('AuditReportView', () => import('./components/Admin/AuditReportView').then((module) => ({ default: module.AuditReportView })))
const InvitesPage = lazyWithStaleChunkRetry<{ onOpenWorkspaceSettings?: () => void }>('InvitesPage', () => import('./components/InvitesUploadLinkPages').then((module) => ({ default: module.InvitesPage })))
const LinkHub = lazyWithStaleChunkRetry('LinkHub', () => import('./components/Storage/LinkHub').then((module) => ({ default: module.LinkHub })))
const StockHub = lazyWithStaleChunkRetry('StockHub', () => import('./components/Stock/StockHub').then((module) => ({ default: module.StockHub })))
const ClientsPage = lazyWithStaleChunkRetry<{ onManagePlan?: () => void }>('ClientsPage', () => import('./components/Clients/ClientsPage').then((module) => ({ default: module.ClientsPage })))
const BudgetHubLayout = lazyWithStaleChunkRetry<{ onManagePlan?: () => void; onOpenWorkspaceSettings?: () => void }>('BudgetHubLayout', () => import('./components/Budget/BudgetHubLayout').then((module) => ({ default: module.BudgetHubLayout })))
const QuotaHubLayout = lazyWithStaleChunkRetry<{ onManagePlan?: () => void; onOpenWorkspaceSettings?: () => void }>('QuotaHubLayout', () => import('./components/Quota/QuotaHubLayout').then((module) => ({ default: module.QuotaHubLayout })))
const EducationHub = lazyWithStaleChunkRetry<EducationHubProps>('EducationHub', () => import('./components/Education/EducationHub').then((module) => ({ default: module.EducationHub })))
const ConstructionHub = lazyWithStaleChunkRetry<ConstructionHubProps>('ConstructionHub', () => import('./components/Ops/ConstructionHub').then((module) => ({ default: module.ConstructionHub })))
const HospitalHub = lazyWithStaleChunkRetry<HospitalHubProps>('HospitalHub', () => import('./components/Ops/HospitalHub').then((module) => ({ default: module.HospitalHub })))
const SitesHub = lazyWithStaleChunkRetry<SitesHubProps>('SitesHub', () => import('./components/Ops/SitesHub').then((module) => ({ default: module.SitesHub })))
const DeliveryHub = lazyWithStaleChunkRetry<DeliveryHubProps>('DeliveryHub', () => import('./components/Ops/DeliveryHub').then((module) => ({ default: module.DeliveryHub })))
const ShopsHub = lazyWithStaleChunkRetry<ShopsHubProps>('ShopsHub', () => import('./components/Ops/ShopsHub').then((module) => ({ default: module.ShopsHub })))
const StaysHub = lazyWithStaleChunkRetry<StaysHubProps>('StaysHub', () => import('./components/Ops/StaysHub').then((module) => ({ default: module.StaysHub })))
const JobsHub = lazyWithStaleChunkRetry<JobsHubProps>('JobsHub', () => import('./components/Ops/JobsHub').then((module) => ({ default: module.JobsHub })))
const ClinicsHub = lazyWithStaleChunkRetry<ClinicsHubProps>('ClinicsHub', () => import('./components/Ops/ClinicsHub').then((module) => ({ default: module.ClinicsHub })))
const PlantHub = lazyWithStaleChunkRetry<PlantHubProps>('PlantHub', () => import('./components/Ops/PlantHub').then((module) => ({ default: module.PlantHub })))
const FitnessHub = lazyWithStaleChunkRetry<FitnessHubProps>('FitnessHub', () => import('./components/Ops/FitnessHub').then((module) => ({ default: module.FitnessHub })))
const TimesheetsHubLayout = lazyWithStaleChunkRetry<{ onManagePlan?: () => void }>('TimesheetsHubLayout', () => import('./components/Timesheets/TimesheetsHubLayout').then((module) => ({ default: module.TimesheetsHubLayout })))
const UploadHubLayout = lazyWithStaleChunkRetry('UploadHubLayout', () => import('./components/Storage/UploadHubLayout').then((module) => ({ default: module.UploadHubLayout })))
const FormsLayout = lazyWithStaleChunkRetry('FormsLayout', () => import('./components/Forms/FormsLayout').then((module) => ({ default: module.FormsLayout })))
const MeetingLayout = lazyWithStaleChunkRetry('MeetingLayout', () => import('./components/Meetings/MeetingLayout').then((module) => ({ default: module.MeetingLayout })))
const AutomationHubLayout = lazyWithStaleChunkRetry('AutomationHubLayout', () => import('./components/Automate/AutomationHubLayout').then((module) => ({ default: module.AutomationHubLayout })))
const DesignHubLayout = lazyWithStaleChunkRetry('DesignHubLayout', () => import('./components/Design/DesignHubLayout').then((module) => ({ default: module.DesignHubLayout })))
const WorkspaceOverviewPage = lazyWithStaleChunkRetry('WorkspaceOverviewPage', () => import('./components/Overview/WorkspaceOverviewPage').then((module) => ({ default: module.WorkspaceOverviewPage })))
const MemberDashboard = lazyWithStaleChunkRetry('MemberDashboard', () => import('./components/Admin/MemberDashboard').then((module) => ({ default: module.MemberDashboard })))
const ChecklistsShell = lazyWithStaleChunkRetry('ChecklistsShell', () => import('./components/ChecklistBoard/ChecklistsShell').then((module) => ({ default: module.ChecklistsShell })))
const BoardExplorer = lazyWithStaleChunkRetry('BoardExplorer', () => import('./components/BoardExplorer').then((module) => ({ default: module.BoardExplorer })))
const ExploreHubPanel = lazyWithStaleChunkRetry('ExploreHubPanel', () => import('./components/ExploreHubPanel').then((module) => ({ default: module.ExploreHubPanel })))
const ChatPage = lazyWithStaleChunkRetry('ChatPage', () => import('./components/Chat/ChatPage').then((module) => ({ default: module.ChatPage })))
const CalendarPage = lazyWithStaleChunkRetry('CalendarPage', () => import('./components/Calendar/CalendarPage').then((module) => ({ default: module.CalendarPage })))
const UserSettingsPage = lazyWithStaleChunkRetry('UserSettingsPage', () => import('./components/Settings/UserSettingsPage').then((module) => ({ default: module.UserSettingsPage })))
const PurchasePage = lazyWithStaleChunkRetry('PurchasePage', () => import('./components/Settings/PurchasePage').then((module) => ({ default: module.PurchasePage })))
const YourPage = lazyWithStaleChunkRetry('YourPage', () => import('./components/Gamification/YourPage').then((module) => ({ default: module.YourPage })))
const TeamRaidDashboard = lazyWithStaleChunkRetry('TeamRaidDashboard', () => import('./components/Gamification/TeamRaidDashboard').then((module) => ({ default: module.TeamRaidDashboard })))
const ForYouPage = lazyWithStaleChunkRetry('ForYouPage', () => import('./components/ForYouPage').then((module) => ({ default: module.ForYouPage })))
const AnalysisHub = lazyWithStaleChunkRetry('AnalysisHub', () => import('./components/Analysis/AnalysisHub').then((module) => ({ default: module.AnalysisHub })))
const ReportCenter = lazyWithStaleChunkRetry('ReportCenter', () => import('./components/Reports/ReportCenter').then((module) => ({ default: module.ReportCenter })))
const FocusExperiencePage = lazyWithStaleChunkRetry<FocusExperiencePageProps>('FocusExperiencePage', () => import('./components/Focus/FocusExperiencePage'))

const formatRange = (item: DisplayRange) => {
  const formatter = new Intl.DateTimeFormat('en', {
    month: 'short',
    year: 'numeric',
  })

  return `${formatter.format(new Date(item.startDate))} - ${formatter.format(new Date(item.endDate))}`
}

const stripManageSegmentFromPath = (pathname: string) => {
  const parts = getConsoleParts(pathname)

  if (parts[0] === 'personal' && parts[2] === 'manage') {
    return toConsolePath(`/${[parts[0], parts[1], ...parts.slice(3)].filter(Boolean).join('/')}`)
  }

  if (parts[0] === 'workspace' && parts[3] === 'manage') {
    return toConsolePath(`/${[parts[0], parts[1], parts[2], ...parts.slice(4)].filter(Boolean).join('/')}`)
  }

  return pathname
}

export const MANAGE_MODE_DEFAULT: ManageWorkspaceMode = 'review'

const readManageModeFromSearch = (): ManageWorkspaceMode => {
  // Manage lands on Review by default; users can flip to Board via the
  // CompareArrows toggle in each view's toolbar. An explicit ?mode=board wins.
  const mode = new URLSearchParams(window.location.search).get('mode')
  return mode === 'board' ? 'board' : MANAGE_MODE_DEFAULT
}

const buildManageRoute = (
  routeBase: string,
  appMode: WorkspaceKind,
  board: TimelineBoardKey,
  mode: ManageWorkspaceMode,
) => {
  if (!routeBase) return null
  const boardCode = BOARD_CODE_BY_KEY[appMode === 'PERSONAL' ? 'DASHBOARD' : board]
  return toConsolePath(`${routeBase}/${boardCode}/manage?mode=${mode}`)
}


function AppContent() {
  const tourGuide = useContext(TourGuideContext)
  const isTourActive = Boolean(tourGuide?.isTourActive)
  const isMobile = useIsMobile()
  const { currentPath } = useCurrentPath()
  const { goals } = useData()
  const { user, userProfile, signOutUser } = useAuth()
  const { t } = useNls()
  const { showToast } = useToast()
  const {
    workspaces,
    activeWorkspaceId,
    activeRole,
    appMode,
    setAppMode,
    canPerform,
    getWorkspaceRole,
    isWorkspaceLoading,
    workspaceLoadingError,
    refreshWorkspaces,
    openPreviewWorkspace,
  } = useWorkspace()
  const { canSwitchToFamily, learners: familyLearners } = useFamilyAccess()
  const { activateExisting: activateExistingPersonal } = useLaunchPersonalWorkspace()
  const {
    currentLevel,
    activeGoalId,
    activeTaskId,
    activeSubTaskId,
    goToDashboard,
    jumpToLevel,
  } = useNavigation()
  const { focusItem, resetViewWindow } = useCanvasScale()
  const {
    elements: timelineElements,
    checklistItems: timelineChecklistItems,
    selectedElementId,
    setSelectedElementId,
    activeCanvasNodeId,
    setActiveCanvasNodeId,
    isCanvasMaximized,
    maximizeCanvasWorkspace,
    closeCanvasWorkspace,
    isAccessRestricted,
  } = useTimeline()
  const manageDraft = useManageDraft()
  const { seedProposedEdits: seedManageDraftProposedEdits } = manageDraft
  const { activeRaid } = useRaid()
  const { notifications, markRead } = useNotifications()
  const { openChecklistDetail } = useChecklistDetailPopup()
  const pendingChecklistOpenRef = useRef<{ itemId: string; periodKey?: string } | null>(null)
  const { setContextBoard, setMeetingContext } = useChat()
  const { canEditElement } = useCanvasPermissions()
  const [aiMagicHandoff, setAiMagicHandoff] = useState<AiChatHandoffRequest | null>(null)
  const [aiMagicOpenSessionRequest, setAiMagicOpenSessionRequest] = useState<{ session: AiChatSession; nonce: number } | null>(null)
  // Detailed View chrome — Manage is the single approval/draft surface for
  // manual edits and AI-generated/edited timeline drafts. Null = closed.
  const [detailedViewChrome, setDetailedViewChrome] = useState<'manage' | null>(null)
  // After Move-to-Manage handoff the parent asks BoardExplorer to open the
  // new/edited goal. Firestore snapshot may lag one tick, so BoardExplorer
  // holds it and applies when the id appears in elementById; when consumed,
  // the parent clears it (via onPendingOpenConsumed) to avoid re-triggering.
  const [pendingBoardOpenId, setPendingBoardOpenId] = useState<string | null>(null)
  const [pendingBoardSelectOnlyId, setPendingBoardSelectOnlyId] = useState<string | null>(null)

  useEffect(() => {
    if (pendingChecklistOpenRef.current && activeWorkspaceId) {
      const pending = pendingChecklistOpenRef.current
      pendingChecklistOpenRef.current = null
      openChecklistDetail(pending.itemId, pending.periodKey ? { initialOccurrenceKey: pending.periodKey } : undefined)
    }
  }, [activeWorkspaceId, openChecklistDetail])
  const [manageActiveElementId, setManageActiveElementId] = useState<string | null>(null)
  const [manageActiveWasExpanded, setManageActiveWasExpanded] = useState<boolean>(false)
  const [boardOpenElementId, setBoardOpenElementId] = usePersistedState<string | null>('boardExplorer.openElementId', null)
  // Detailed View Screen — Manage owns AI-generated/edit draft handling.
  const [detailedViewMode, setDetailedViewMode] = useState<DetailedViewMode | null>(null)
  const [detailedViewWorkingGoal, setDetailedViewWorkingGoal] = useState<Goal | null>(null)
  const [detailedViewBaseline, setDetailedViewBaseline] = useState<Goal | null>(null)
  const [detailedViewTargetBoard, setDetailedViewTargetBoard] = useState<TimelineBoardKey | null>(null)
  // Workspace mode — Manage's "Advanced" entry brings up the multi-goal view.
  const [detailedViewWorkingGoals, setDetailedViewWorkingGoals] = useState<Goal[] | null>(null)
  const [detailedViewBaselineGoals, setDetailedViewBaselineGoals] = useState<Goal[] | null>(null)
  // Which live goals Review's workspace draft mirrors (H-1): every board, or only DASHBOARD
  // for the dashboard-move entry. Null when Review isn't seeded from live data.
  const [detailedViewLiveScope, setDetailedViewLiveScope] = useState<'ALL' | 'DASHBOARD' | null>(null)
  const [detailedViewInitialFocus, setDetailedViewInitialFocus] = useState<DetailedSelection | null>(null)
  // Post-flight validation issues + AI's own commentary + ids the AI changed.
  // Manage pulls all three: issues drive the yellow warning markers and global
  // Save lock; aiNote shows as a banner; changedIds mark AI-touched draft rows.
  const [detailedViewInitialIssues, setDetailedViewInitialIssues] = useState<unknown[] | null>(null)
  const [detailedViewAiNote, setDetailedViewAiNote] = useState<string | null>(null)
  const [detailedViewAiNewIds, setDetailedViewAiNewIds] = useState<string[] | null>(null)
  const [detailedViewAiChangedIds, setDetailedViewAiChangedIds] = useState<string[] | null>(null)
  const [detailedViewAiConfidence, setDetailedViewAiConfidence] = useState<number | null>(null)
  const [detailedViewOperationalProposal, setDetailedViewOperationalProposal] = useState<AiOperationalProposalMetadata | null>(null)
  // Recurring template overlay retired — books now render inside ChecklistsShell.
  const [isWorkspaceMenuOpen, setIsWorkspaceMenuOpen] = useState(false)
  const [leavingWorkspace, setLeavingWorkspace] = useState<{ id: string; name: string } | null>(null)
  const [, setIsProfileMenuOpen] = useState(false)
  const [, setPendingInvites] = useState<PendingInvite[]>([])
  const [, setIncomingJoinRequests] = useState<IncomingJoinRequest[]>([])
  const creditsContext = useCredits()
  const globalPersonalCredits = creditsContext.personalCredits
  const [uploadArtifactFilter, setUploadArtifactFilter] = useState<{ artifactType?: string; artifactId: string } | null>(null)
  const [pendingMeetingId, setPendingMeetingId] = useState<string | null>(null)
  const [selectedStockItemId, setSelectedStockItemId] = useState<string | null>(null)
  const [purchaseTab, setPurchaseTab] = usePersistedState<PurchaseTabKey>('app.purchaseTab', 'TEAM')
  const [activeLayoutView, setActiveLayoutView] = useState<ActiveLayoutView>('DASHBOARD')
  const [userSettingsInitialSection, setUserSettingsInitialSection] = useState<SettingsSection>('GENERAL')
  const [workspaceSettingsInitialSection, setWorkspaceSettingsInitialSection] = useState<WorkspaceSettingsSection>('OVERVIEW')
  const [pendingCharacterHighlight, setPendingCharacterHighlight] = useState<
    { kind: 'THEME' | 'BACKGROUND' | 'SOUNDSCAPE'; cosmeticId: string } | null
  >(null)
  const [pendingEduTab, setPendingEduTab] = useState<EducationSubTab | null>(null)
  const handleClearPendingEduTab = useCallback(() => {
    setPendingEduTab(null)
  }, [])
  const [pendingOpsTab, setPendingOpsTab] = useState<OpsTab | null>(null)
  const handleClearPendingOpsTab = useCallback(() => {
    setPendingOpsTab(null)
  }, [])
  const preChatRouteRef = useRef<string | null>(null)
  const [reportRoute, setReportRoute] = usePersistedState<{ page: 'HUB' | 'NEW' | 'PREVIEW' | 'DETAIL'; reportId: string | null }>('app.reportRoute', { page: 'HUB', reportId: null })
  const [auditReportId, setAuditReportId] = useState<string | null>(null)
  const [pendingDashboardView, setPendingDashboardView] = useState<DashboardView | null>(null)
  const [activeDashboardView, setActiveDashboardView] = usePersistedState<DashboardView | null>('app.activeDashboardView', null)
  const { isMobileViewport } = useClientPreferences()
  const [activeBoard, setActiveBoard] = useState<TimelineBoardKey>('DASHBOARD')
  const [manageMode, setManageMode] = useState<ManageWorkspaceMode>(MANAGE_MODE_DEFAULT)
  const [routeNotFoundPath, setRouteNotFoundPath] = useState<string | null>(null)
  const [restrictedWorkspaceRoute, setRestrictedWorkspaceRoute] = useState<RestrictedWorkspaceRoute | null>(null)
  const [recentWorkspaceIds, setRecentWorkspaceIds] = useState<string[]>(() => getRecentWorkspaceIds())
  const [isWatchingActiveItem, setIsWatchingActiveItem] = useState(false)
  const [mobileTabBarHeight, setMobileTabBarHeight] = useState<number>(0)
  const [mobileHeaderHeight, setMobileHeaderHeight] = useState<number>(0)
  const headerRef = useRef<HTMLElement | null>(null)
  const workspaceMenuButtonRef = useRef<HTMLButtonElement | null>(null)
  const canMutate = canPerform('timeline.edit')
  const canOpenAdminSettings = canPerform('workspace.edit_settings')
    || canPerform('workspace.edit_team_settings')
    || canPerform('workspace.manage_members')
    || canPerform('workspace.manage_invites')
    || canPerform('workspace.manage_field_passes')
    || canPerform('workspace.manage_permissions')
    || canPerform('workspace.manage_credits')
  const teamWorkspaces = workspaces.filter((workspace) => isTeamWorkspace(workspace))
  const personalWorkspace = workspaces.find((workspace) => isPersonalWorkspace(workspace))
  const ownsPersonalWorkspace = Boolean(user && workspaces.some((workspace) =>
    !workspace.isPreview && workspace.ownerId === user.uid && isPersonalWorkspace(workspace)
  ))
  const ownsTeamWorkspace = Boolean(user && workspaces.some((workspace) =>
    !workspace.isPreview && workspace.ownerId === user.uid && isTeamWorkspace(workspace)
  ))
  const canCreatePersonalWorkspace = !ownsPersonalWorkspace
  const canCreateTeamWorkspace = !ownsTeamWorkspace
  const canCreateWorkspace = canCreatePersonalWorkspace || canCreateTeamWorkspace
  const activeWorkspace = workspaces.find((workspace) => workspace.id === activeWorkspaceId)
  const activeWorkspaceIsPersonal = isPersonalWorkspace(activeWorkspace)
  const expandDashboardViewKeys = (keys: readonly DashboardViewKey[]): DashboardViewKey[] => {
    if (!keys.includes('HEATMAP') || keys.includes('GRID')) return [...keys]
    const expanded: DashboardViewKey[] = []
    keys.forEach((key) => {
      if (key === 'HEATMAP') expanded.push('GRID')
      expanded.push(key)
    })
    return expanded
  }
  const dockEnabledViews = useMemo<DashboardView[]>(() => {
    const funPackOff = switchedOffModulesFor(activeWorkspace).has('funPack')
    const keys = expandDashboardViewKeys(activeWorkspace?.enabledDashboardViews?.length
      ? activeWorkspace.enabledDashboardViews
      : AVAILABLE_VIEW_KEYS
    ).filter((key: DashboardViewKey) => !COMING_SOON_VIEW_KEYS.has(key) && !(funPackOff && key === 'LEADERBOARD'))
    return keys.map((key) => DASHBOARD_VIEW_MAP[key]).filter((view): view is DashboardView => Boolean(view))
  }, [activeWorkspace])
  const currentViewRequiresReal = viewRequiresRealWorkspace(activeLayoutView)
  const realSwitchWorkspaces = workspaces.filter((workspace) => !workspace.isPreview)
  const previewSwitchWorkspaces = currentViewRequiresReal
    ? []
    : teamWorkspaces.filter((workspace) => workspace.isPreview)
  const recentPreviewWorkspaces = recentWorkspaceIds
    .map((workspaceId) => previewSwitchWorkspaces.find((workspace) => workspace.id === workspaceId))
    .filter((workspace): workspace is NonNullable<typeof workspace> => Boolean(workspace))
  const otherRealSwitchWorkspaces = realSwitchWorkspaces.filter((workspace) => workspace.id !== activeWorkspaceId)
  const otherPreviewSwitchWorkspaces = previewSwitchWorkspaces
    .filter((workspace) => workspace.id !== activeWorkspaceId)
    .sort((left, right) => {
      const leftIndex = recentPreviewWorkspaces.findIndex((workspace) => workspace.id === left.id)
      const rightIndex = recentPreviewWorkspaces.findIndex((workspace) => workspace.id === right.id)
      return (leftIndex < 0 ? Number.POSITIVE_INFINITY : leftIndex)
        - (rightIndex < 0 ? Number.POSITIVE_INFINITY : rightIndex)
    })
  const workspaceMenuRows = activeWorkspace ? [activeWorkspace] : []
  const activeWorkspaceIsPreview = activeWorkspace?.isPreview === true
  const activeWorkspacePreviewMask = activeWorkspace?.previewSurfaceMask
  const hasRealWorkspaces = useMemo(
    () => workspaces.some((workspace) => !workspace.isPreview),
    [workspaces],
  )
  const switchedOffModules = useMemo(
    () => switchedOffModulesFor(activeWorkspace),
    [activeWorkspace],
  )
  const routeBase = appMode === 'PERSONAL' ? '/personal' : activeWorkspace?.slug ? `/workspace/${activeWorkspace.slug}` : ''
  const {
    setIsAdminPanelOpen,
    setIsMemberPanelOpen,
    openWorkspaceSettings,
    closeWorkspacePanels,
    workspaceSettingsIntent,
    consumeWorkspaceSettingsIntent,
  } = useAppLifecycle({
    activeWorkspaceId,
    activeRole,
    currentUser: user,
    inviteToken: null,
    onClearInvite: () => undefined,
  })

  useEffect(() => {
    const applyReportHash = () => {
      const hash = window.location.hash.replace(/^#/, '')
      if (isWorkspaceLoading) return
      if (hash.startsWith('/audit/report/')) {
        const reportId = hash.split('/').filter(Boolean)[2] ?? null
        if (!roleCanPerform(activeRole, 'ADMIN') || appMode !== 'TEAM' || !reportId) {
          window.location.hash = ''
          return
        }
        setAuditReportId(reportId)
        setActiveLayoutView('SUMMARY')
        return
      }
      if (!hash.startsWith('/reports')) return
      setAuditReportId(null)
      if (!canMutate || appMode !== 'TEAM') {
        window.location.hash = ''
        return
      }
      const parts = hash.split('/').filter(Boolean)
      setReportRoute(parts[1] === 'new'
        ? { page: 'NEW', reportId: null }
        : parts[1] === 'preview' && parts[2]
          ? { page: 'PREVIEW', reportId: parts[2] }
          : { page: 'HUB', reportId: null })
      setActiveLayoutView('REPORTS')
    }
    applyReportHash()
    window.addEventListener('hashchange', applyReportHash)
    return () => window.removeEventListener('hashchange', applyReportHash)
  }, [activeRole, appMode, canMutate, isWorkspaceLoading])

  useEffect(() => {
    const preventPageZoom = (event: globalThis.WheelEvent) => {
      const target = event.target instanceof Element
        ? event.target.closest('[data-dashboard-viewport="true"]')
        : null
      if (target) return

      if (event.ctrlKey || event.metaKey) {
        event.preventDefault()
      }
    }
    const preventGestureZoom = (event: Event) => {
      const target = event.target instanceof Element
        ? event.target.closest('[data-dashboard-viewport="true"]')
        : null
      if (target) return

      event.preventDefault()
    }
    const listenerOptions = { passive: false, capture: true } as AddEventListenerOptions

    window.addEventListener('wheel', preventPageZoom, listenerOptions)
    window.addEventListener('gesturestart', preventGestureZoom, listenerOptions)
    window.addEventListener('gesturechange', preventGestureZoom, listenerOptions)
    window.addEventListener('gestureend', preventGestureZoom, listenerOptions)

    return () => {
      window.removeEventListener('wheel', preventPageZoom, listenerOptions)
      window.removeEventListener('gesturestart', preventGestureZoom, listenerOptions)
      window.removeEventListener('gesturechange', preventGestureZoom, listenerOptions)
      window.removeEventListener('gestureend', preventGestureZoom, listenerOptions)
    }
  }, [])

  useEffect(() => {
    if (!activeWorkspaceId) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Synchronizes local storage recent workspaces with state on active workspace change
    setRecentWorkspaceIds(recordRecentWorkspaceId(activeWorkspaceId))
  }, [activeWorkspaceId])


  useEffect(() => {
    const closeFloatingMenus = (event: PointerEvent) => {
      const target = event.target instanceof Element ? event.target : null
      if (target?.closest('[data-floating-menu-root="true"]')) return

      setIsWorkspaceMenuOpen(false)
      setIsProfileMenuOpen(false)
    }

    window.addEventListener('pointerdown', closeFloatingMenus)
    return () => window.removeEventListener('pointerdown', closeFloatingMenus)
  }, [])

  useEffect(() => {
    const headerElement = headerRef.current
    if (!headerElement) return

    const publishHeaderOffset = () => {
      const rect = headerElement.getBoundingClientRect()
      const bottom = rect.bottom
      const height = rect.height
      document.documentElement.style.setProperty('--app-header-offset', `${Math.max(0, Math.round(bottom))}px`)
      document.documentElement.style.setProperty('--app-header-height', `${Math.max(0, Math.round(height))}px`)
      setMobileHeaderHeight(Math.max(0, Math.round(height)))
    }

    publishHeaderOffset()
    const observer = new ResizeObserver(publishHeaderOffset)
    observer.observe(headerElement)
    window.addEventListener('resize', publishHeaderOffset)

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', publishHeaderOffset)
    }
  }, [])

  useEffect(() => {
    if (mobileTabBarHeight > 0) {
      document.documentElement.style.setProperty('--mobile-tab-bar-height', `${mobileTabBarHeight}px`)
    }
  }, [mobileTabBarHeight])

  const allTimelineElementById = useMemo(
    () => new Map(timelineElements.map((element) => [element.id, element])),
    [timelineElements],
  )
  const boardOpenElement = boardOpenElementId ? allTimelineElementById.get(boardOpenElementId) ?? null : null
  const boardOpenChain = useMemo(
    () => computeOpenChain(boardOpenElement, allTimelineElementById),
    [allTimelineElementById, boardOpenElement],
  )
  const timelineElementByRoutePath = useMemo(
    () => new Map(timelineElements
      .filter((element) => (element.board ?? 'DASHBOARD') === activeBoard && element.routePath)
      .map((element) => [element.routePath ?? '', element])),
    [activeBoard, timelineElements],
  )

  const routeFromNavigation = useCallback(() => {
    if (routeNotFoundPath || restrictedWorkspaceRoute) return window.location.pathname
    const currentParts = getConsoleParts()
    if (currentParts[0] === 'workspace' && currentParts[1] && activeWorkspace?.slug !== currentParts[1]) {
      return window.location.pathname
    }
    const topLevelViews: Partial<Record<string, ActiveLayoutView>> = {
      explore: 'EXPLORE',
      settings: 'USER_SETTINGS',
      your: 'YOUR_PAGE',
      purchase: 'PURCHASE',
      invites: 'INVITES',
      members: 'INVITES',
      team: 'INVITES',
      focus: 'FOCUS',
    }
    const topLevelRouteView = topLevelViews[currentParts[0]]
    if (topLevelRouteView && activeLayoutView !== topLevelRouteView) {
      return window.location.pathname
    }
    const workspaceTopLevelViews: Partial<Record<string, ActiveLayoutView>> = {
      link: 'LINK',
      stock: 'STOCK',
      clients: 'CLIENTS',
      timesheets: 'TIMESHEETS',
      budget: 'BUDGET',
      quota: 'QUOTA',
      teaching: 'TEACHING',
      training: 'TRAINING',
      ...Object.fromEntries(
        OPS_SECTOR_KEYS.map((s) => [OPS_SECTORS[s].routeSegment, OPS_SECTORS[s].view]),
      ),
      upload: 'UPLOAD',
      uploads: 'UPLOAD',
      forms: 'FORMS',
      meetings: 'MEETINGS',
      automate: 'AUTOMATE',
      design: 'DESIGN',
      chat: 'CHAT',
      calendar: 'CALENDAR',
      'for-you': 'FOR_YOU',
      overview: 'OVERVIEW',
      analysis: 'ANALYSIS',
    }
    const workspaceRouteView = currentParts[0] === 'workspace' && currentParts[1] === activeWorkspace?.slug
      ? workspaceTopLevelViews[currentParts[2]]
      : undefined
    if (workspaceRouteView && activeLayoutView !== workspaceRouteView) {
      return window.location.pathname
    }
    const boardRouteViews: Partial<Record<string, ActiveLayoutView>> = {
      ai: 'AI_MAGIC',
      manage: 'MANAGE',
      raid: 'RAID',
      summary: 'SUMMARY',
      reports: 'REPORTS',
      checklists: 'CHECKLISTS',
    }
    if (currentParts[0] === 'workspace' && currentParts[1] === activeWorkspace?.slug) {
      if (currentParts[2] === 'manage' && currentParts[3] === 'audit' && activeLayoutView !== 'SUMMARY') {
        return window.location.pathname
      }
      const currentBoard = parseBoardCode(currentParts[2])
      const boardRouteView = currentBoard
        ? currentParts[3] === 'settings'
          ? 'WORKSPACE_SETTINGS'
          : boardRouteViews[currentParts[3]]
        : undefined
      if (boardRouteView && activeLayoutView !== boardRouteView) {
        return window.location.pathname
      }
    }
    if (activeLayoutView === 'EXPLORE') return currentParts[0] === 'explore' ? window.location.pathname : toConsolePath('/explore')
    if (activeLayoutView === 'USER_SETTINGS') return toConsolePath('/settings')
    if (activeLayoutView === 'YOUR_PAGE') return toConsolePath('/your')
    if (activeLayoutView === 'PURCHASE') return toConsolePath(purchaseTab === 'STORAGE' ? '/purchase/storage' : '/purchase')
    if (activeLayoutView === 'INVITES') return toConsolePath('/team')
    if (activeLayoutView === 'FOCUS') return toConsolePath('/focus')
    if (activeLayoutView === 'LINK') return routeBase ? toConsolePath(`${routeBase}/link`) : '/console'
    if (activeLayoutView === 'STOCK') return routeBase ? toConsolePath(`${routeBase}/stock`) : '/console'
    if (activeLayoutView === 'CLIENTS') return routeBase ? toConsolePath(`${routeBase}/clients`) : '/console'
    if (activeLayoutView === 'TIMESHEETS') return routeBase ? toConsolePath(`${routeBase}/timesheets`) : '/console'
    if (activeLayoutView === 'BUDGET') return routeBase ? toConsolePath(`${routeBase}/budget`) : '/console'
    if (activeLayoutView === 'QUOTA') return routeBase ? toConsolePath(`${routeBase}/quota`) : '/console'
    if (activeLayoutView === 'TEACHING') return routeBase ? toConsolePath(`${routeBase}/teaching`) : '/console'
    if (activeLayoutView === 'TRAINING') return routeBase ? toConsolePath(`${routeBase}/training`) : '/console'
    const activeOpsSector = findOpsSectorByView(activeLayoutView)
    if (activeOpsSector) return routeBase ? toConsolePath(`${routeBase}/${OPS_SECTORS[activeOpsSector].routeSegment}`) : '/console'
    if (activeLayoutView === 'UPLOAD') return routeBase ? toConsolePath(`${routeBase}/upload`) : '/console'
    if (activeLayoutView === 'FORMS') return routeBase ? toConsolePath(`${routeBase}/forms`) : '/console'
    if (activeLayoutView === 'MEETINGS') return routeBase ? toConsolePath(`${routeBase}/meetings`) : '/console'
    if (activeLayoutView === 'AUTOMATE') return routeBase ? toConsolePath(`${routeBase}/automate`) : '/console'
    if (activeLayoutView === 'DESIGN') return routeBase ? toConsolePath(`${routeBase}/design`) : '/console'
    if (activeLayoutView === 'OVERVIEW') return routeBase ? toConsolePath(`${routeBase}/overview`) : '/console'
    if (activeLayoutView === 'ANALYSIS') return routeBase ? toConsolePath(`${routeBase}/analysis`) : '/console'
    // Personal workspaces don't own Visionboard / Archive / Bin — clamp the
    // board code here so a stale activeBoard from a previous team workspace
    // never produces an invalid `/personal/visionboard` URL that the route
    // listener would then loop on.
    const effectiveBoard: TimelineBoardKey = appMode === 'PERSONAL' ? 'DASHBOARD' : activeBoard
    const boardCode = BOARD_CODE_BY_KEY[effectiveBoard]
    if (!routeBase) return '/console'
    if (activeLayoutView === 'WORKSPACE_SETTINGS') {
      return toConsolePath(appMode === 'PERSONAL' ? `${routeBase}/settings` : `${routeBase}/${boardCode}/settings/${roleCanPerform(activeRole, 'ADMIN') ? 'admin' : 'member'}`)
    }
    if (activeLayoutView === 'SUMMARY') return toConsolePath(`${routeBase}/manage/audit`)
    if (activeLayoutView === 'REPORTS') return toConsolePath(`${routeBase}/${boardCode}/reports`)
    if (activeLayoutView === 'AI_MAGIC') return toConsolePath(`${routeBase}/${boardCode}/ai`)
    if (activeLayoutView === 'MANAGE') return toConsolePath(`${routeBase}/${boardCode}/manage?mode=${manageMode}`)
    if (activeLayoutView === 'CHECKLISTS') return toConsolePath(`${routeBase}/${boardCode}/checklists`)
    if (activeLayoutView === 'CHAT') return toConsolePath(`${routeBase}/chat`)
    if (activeLayoutView === 'CALENDAR') return toConsolePath(`${routeBase}/calendar`)
    if (activeLayoutView === 'RAID') return toConsolePath(`${routeBase}/${boardCode}/raid`)
    if (activeLayoutView === 'FOR_YOU') return toConsolePath(`${routeBase}/for-you`)
    const activeElement =
      currentLevel === 'goal' && activeGoalId ? allTimelineElementById.get(activeGoalId) :
        currentLevel === 'task' && activeTaskId ? allTimelineElementById.get(activeTaskId) :
          currentLevel === 'subtask' && activeSubTaskId ? allTimelineElementById.get(activeSubTaskId) :
            null
    const routePath = activeElement?.routePath ? `/${activeElement.routePath}` : ''
    return toConsolePath(`${routeBase}/${boardCode}${routePath}`)
  }, [
    activeBoard,
    activeGoalId,
    activeLayoutView,
    activeSubTaskId,
    activeTaskId,
    allTimelineElementById,
    appMode,
    currentLevel,
    manageMode,
    purchaseTab,
    activeRole,
    activeWorkspace?.slug,
    restrictedWorkspaceRoute,
    routeNotFoundPath,
    routeBase,
  ])

  const closeDetailedView = useCallback(() => {
    if (detailedViewChrome === 'manage') {
      const nextPath = stripManageSegmentFromPath(window.location.pathname)
      if (nextPath !== window.location.pathname) {
        window.history.replaceState(null, '', nextPath)
      }
    }

    setDetailedViewMode(null)
    setDetailedViewChrome(null)
    setDetailedViewWorkingGoal(null)
    setDetailedViewBaseline(null)
    setDetailedViewTargetBoard(null)
    setDetailedViewWorkingGoals(null)
    setDetailedViewBaselineGoals(null)
    setDetailedViewLiveScope(null)
    setDetailedViewInitialFocus(null)
    setDetailedViewInitialIssues(null)
    setDetailedViewAiNote(null)
    setDetailedViewAiNewIds(null)
    setDetailedViewAiChangedIds(null)
    setDetailedViewAiConfidence(null)
    setDetailedViewOperationalProposal(null)
  }, [detailedViewChrome])

  const routeForWorkspaceRoot = useCallback((workspace: typeof workspaces[number], board: TimelineBoardKey = 'DASHBOARD') => {
    return workspaceRootPath(workspace, board)
  }, [])

  const switchToWorkspace = useCallback(async (workspace: typeof workspaces[number], requestedView?: ActiveLayoutView) => {
    const nextBoard: TimelineBoardKey = 'DASHBOARD'
    const keepChatOpen = isTeamWorkspace(workspace) && (requestedView === 'CHAT' || (!requestedView && activeLayoutView === 'CHAT'))
    const nextPath = keepChatOpen ? toConsolePath(`/workspace/${workspace.slug}/chat`) : routeForWorkspaceRoot(workspace, nextBoard)
    const targetRole = getWorkspaceRole(workspace.id)
    const landing = resolveSwitchLanding({
      app: 'WEB',
      current: { activeLayoutView: requestedView ?? activeLayoutView, board: activeBoard },
      target: workspace,
      role: targetRole,
    })

    const res = await requestWorkspaceSwitch({
      toWorkspaceId: workspace.id,
      source: 'USER',
      landing,
      beforeCommit: () => {
        if (window.location.pathname !== nextPath) {
          window.history.pushState(
            keepChatOpen ? { ...window.history.state, chatReturnRoute: preChatRouteRef.current } : null,
            '',
            nextPath,
          )
        }
      },
    })
    if (res !== 'SWITCHED' && res !== 'NOOP') return

    closeWorkspacePanels()
    closeDetailedView()
    setRestrictedWorkspaceRoute(null)
    setRouteNotFoundPath(null)
    setIsWorkspaceMenuOpen(false)
    setIsProfileMenuOpen(false)
    setActiveLayoutView(keepChatOpen ? 'CHAT' : 'DASHBOARD')
    setActiveBoard(nextBoard)
    setSelectedElementId(null)
    goToDashboard()
    setAppMode(isPersonalWorkspace(workspace) ? 'PERSONAL' : 'TEAM')
  }, [
    activeBoard,
    activeLayoutView,
    closeDetailedView,
    closeWorkspacePanels,
    getWorkspaceRole,
    goToDashboard,
    routeForWorkspaceRoot,
    setAppMode,
    setSelectedElementId,
  ])

  const createWorkspaceOnboarding = useCreateWorkspaceOnboarding({
    canCreate: canCreateWorkspace,
    onWorkspaceReady: switchToWorkspace,
  })

  useWelcomeTourAutoStart({
    activeWorkspaceId,
    isWorkspaceLoading,
    isMobile,
    currentPath,
    hasOnboardingGate: Boolean(createWorkspaceOnboarding.gate || (activeWorkspace && shouldShowWorkspaceWelcome(user?.uid ?? null, activeWorkspace.id))),
    hasOpenModal: Boolean(leavingWorkspace || (typeof document !== 'undefined' && document.querySelector('dialog[open], [role="dialog"]'))),
    userId: user?.uid ?? null,
    startTour: tourGuide?.startTour,
  })

  const openChatPage = useCallback((workspaceId?: string | null) => {
    const targetWorkspace = workspaceId
      ? workspaces.find((workspace) => workspace.id === workspaceId && isTeamWorkspace(workspace))
      : activeWorkspace && isTeamWorkspace(activeWorkspace) ? activeWorkspace : null
    if (!targetWorkspace) return
    if (activeLayoutView !== 'CHAT') {
      preChatRouteRef.current = `${window.location.pathname}${window.location.search}${window.location.hash}`
    }
    switchToWorkspace(targetWorkspace, 'CHAT')
  }, [activeLayoutView, activeWorkspace, switchToWorkspace, workspaces])

  const lastSettledWebPathRef = useRef<string>(
    typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}${window.location.hash}`
      : '',
  )

  useEffect(() => {
    const applyPathRoute = (event?: unknown) => {
      const wasBack = Boolean(
        event &&
        typeof event === 'object' &&
        'isTrusted' in event &&
        (event as { isTrusted?: unknown }).isTrusted === true,
      )
      const deepLinkAction = consumeExploreDeepLink(window.location, window.history, {
        formatNotFoundToast: (id) => t('NLSEduDemoSeedNotFoundToast', { id }),
      })
      if (deepLinkAction.type === 'OPEN_PREVIEW') {
        openPreviewWorkspace(deepLinkAction.previewWorkspace)
        setActiveLayoutView(deepLinkAction.landingView)
        return
      }
      if (deepLinkAction.type === 'EXPLORE_NOT_FOUND') {
        setActiveLayoutView(deepLinkAction.landingView)
        showToast(deepLinkAction.toastMessage)
        return
      }

      const checklistLink = typeof parseChecklistDeepLink !== 'undefined' ? parseChecklistDeepLink(window.location) : null
      if (checklistLink) {
        if (checklistLink.workspaceId && checklistLink.workspaceId !== activeWorkspaceId) {
          const targetWs = workspaces.find((w) => w.id === checklistLink.workspaceId || w.slug === checklistLink.workspaceId)
          if (targetWs) {
            pendingChecklistOpenRef.current = { itemId: checklistLink.itemId, periodKey: checklistLink.periodKey }
            switchToWorkspace(targetWs)
            return
          }
        }
        openChecklistDetail(checklistLink.itemId, checklistLink.periodKey ? { initialOccurrenceKey: checklistLink.periodKey } : undefined)
        try {
          const url = new URL(window.location.href)
          url.searchParams.delete('item')
          url.searchParams.delete('day')
          url.searchParams.delete('week')
          url.searchParams.delete('ws')
          url.searchParams.delete('workspaceId')
          const nextSearch = url.searchParams.toString()
          const nextUrl = url.pathname + (nextSearch ? `?${nextSearch}` : '') + url.hash
          window.history.replaceState(window.history.state, '', nextUrl)
        } catch {
          // ignore in environments without valid URL
        }
      }

      const parts = getConsoleParts()
      setRouteNotFoundPath(null)

      if (parts[0] === 'explore') {
        setActiveLayoutView('EXPLORE')
        return
      }
      if (parts[0] === 'settings') {
        setActiveLayoutView('USER_SETTINGS')
        return
      }
      if (parts[0] === 'your') {
        setActiveLayoutView('YOUR_PAGE')
        return
      }
      if (parts[0] === 'focus') {
        setActiveLayoutView('FOCUS')
        return
      }
      if (parts[0] === 'purchase') {
        setPurchaseTab(parts[1] === 'storage' ? 'STORAGE' : 'TEAM')
        setActiveLayoutView('PURCHASE')
        return
      }
      if (parts[0] === 'invites' || parts[0] === 'members' || parts[0] === 'team') {
        const redirect = legacyTeamRedirect(parts, window.location.search)
        if (redirect) {
          window.history.replaceState(window.history.state, '', redirect)
        }
        setActiveLayoutView('INVITES')
        return
      }
      if (parts[0] === 'link-hub' && parts[1] === 'callback') {
        if (routeBase && appMode === 'TEAM') {
          window.history.replaceState(window.history.state, '', toConsolePath(`${routeBase}/link${window.location.search}`))
          setActiveLayoutView('LINK')
        } else {
          setRouteNotFoundPath(window.location.pathname)
        }
        return
      }
      if (parts[0] === 'upload') {
        if (routeBase) { window.history.replaceState(null, '', toConsolePath(`${routeBase}/upload`)); setActiveLayoutView('UPLOAD') }
        else setRouteNotFoundPath(window.location.pathname)
        return
      }
      if (parts[0] === 'link') {
        if (routeBase) { window.history.replaceState(null, '', toConsolePath(`${routeBase}/link`)); setActiveLayoutView('LINK') }
        else setRouteNotFoundPath(window.location.pathname)
        return
      }
      if (parts[0] === 'personal') {
        setRestrictedWorkspaceRoute(null)
        if (personalWorkspace && activeWorkspaceId !== personalWorkspace.id) {
          const settled = lastSettledWebPathRef.current
          void requestWorkspaceSwitch({
            toWorkspaceId: personalWorkspace.id,
            source: 'URL',
            restoreOnStay: buildUrlRestoreOnStay({
              wasBack,
              settledPath: settled,
            }),
          }).then((res) => {
            if (res === 'SWITCHED') {
              setAppMode('PERSONAL')
            }
          })
        } else if (typeof window !== 'undefined') {
          lastSettledWebPathRef.current = `${window.location.pathname}${window.location.search}${window.location.hash}`
        }
        if (parts[1] === 'forms') {
          setActiveLayoutView('FORMS')
          return
        }
        if (parts[1] === 'meetings') {
          setActiveLayoutView('MEETINGS')
          return
        }
        if (parts[1] === 'automate') {
          setActiveLayoutView('AUTOMATE')
          return
        }
        if (parts[1] === 'settings') {
          setWorkspaceSettingsInitialSection('OVERVIEW')
          setActiveLayoutView('WORKSPACE_SETTINGS')
          setIsAdminPanelOpen(true)
          return
        }
        // Personal workspaces only own the Dashboard board. A stale URL like
        // `/personal/visionboard` (e.g. from session storage carrying over a
        // previous team workspace) used to flip activeBoard back to VISIONBOARD
        // here, then the route-push effect would mirror that into the URL and
        // BoardExplorer would render against a personal workspace it doesn't
        // support. Clamp the parsed board to DASHBOARD instead.
        const parsedPersonalBoard = parseBoardCode(parts[1])
        const personalBoard: TimelineBoardKey | null = parsedPersonalBoard && parsedPersonalBoard !== 'DASHBOARD'
          ? 'DASHBOARD'
          : parsedPersonalBoard
        if (personalBoard) setActiveBoard(personalBoard)
        if (parts[2] === 'ai') {
          setActiveLayoutView('AI_MAGIC')
          return
        }
        if (parts[2] === 'calendar') {
          setActiveLayoutView('CALENDAR')
          return
        }
        if (parts[2] === 'manage') {
          const requestedManageMode = readManageModeFromSearch()
          if (requestedManageMode === 'board') {
            seedDetailedViewForManage()
            setManageMode('board')
            setActiveLayoutView('MANAGE')
            return
          }
          openManageDrawer()
          return
        }
        if (parts[2] === 'checklists') {
          setActiveLayoutView('CHECKLISTS')
          maximizeCanvasWorkspace(WORKSPACE_DEFAULT_CHECKLIST_ID)
          return
        }
        if (parts[2] === 'summary') {
          setActiveLayoutView('SUMMARY')
          return
        }
        if (parts[2] === 'reports') {
          setActiveLayoutView('REPORTS')
          return
        }
        setActiveLayoutView('DASHBOARD')

        const routePath = parts.slice(2).join('/')
        if (!routePath) {
          jumpToLevel('dashboard')
          return
        }
        const element = timelineElementByRoutePath.get(routePath)
        if (!element) {
          setRouteNotFoundPath(window.location.pathname)
          jumpToLevel('dashboard')
          return
        }
        setSelectedElementId(element.id)
        if (element.type === 'GOAL') jumpToLevel('goal', { goalId: element.id })
        if (element.type === 'TASK' && element.parentId) {
          jumpToLevel('task', { goalId: element.parentId, taskId: element.id })
        }
        if (element.type === 'SUBTASK' && element.parentId) {
          const parentTask = allTimelineElementById.get(element.parentId)
          if (parentTask?.parentId) {
            jumpToLevel('subtask', { goalId: parentTask.parentId, taskId: parentTask.id, subTaskId: element.id })
          }
        }
        return
      }
      if (parts[0] !== 'workspace') {
        jumpToLevel('dashboard')
        return
      }

      const slug = parts[1]
      const targetWorkspace = workspaces.find((workspace) => workspace.slug === slug)
      setRestrictedWorkspaceRoute(null)
      if (targetWorkspace && targetWorkspace.id !== activeWorkspaceId) {
        const settled = lastSettledWebPathRef.current
        void requestWorkspaceSwitch({
          toWorkspaceId: targetWorkspace.id,
          source: 'URL',
          restoreOnStay: buildUrlRestoreOnStay({
            wasBack,
            settledPath: settled,
          }),
        }).then((res) => {
          if (res === 'SWITCHED') {
            setAppMode(isPersonalWorkspace(targetWorkspace) ? 'PERSONAL' : 'TEAM')
          }
        })
      } else if (targetWorkspace && typeof window !== 'undefined') {
        lastSettledWebPathRef.current = `${window.location.pathname}${window.location.search}${window.location.hash}`
      } else if (!targetWorkspace && slug) {
        void getDoc(doc(firestoreDb, 'workspace_slugs', slug)).then(async (slugSnapshot) => {
          const workspaceId = typeof slugSnapshot.data()?.workspaceId === 'string'
            ? slugSnapshot.data()?.workspaceId as string
            : ''
          if (!workspaceId) {
            setRouteNotFoundPath(window.location.pathname)
            return
          }
          const workspaceSnapshot = await getDoc(doc(firestoreDb, 'workspaces', workspaceId))
          const data = workspaceSnapshot.data()
          setRestrictedWorkspaceRoute({
            workspaceId,
            slug,
            name: typeof data?.name === 'string' ? data.name : slug,
            isPublic: data?.isPublic === true,
          })
        }).catch(() => setRouteNotFoundPath(window.location.pathname))
      }

      if (parts[2] === 'chat') {
        setActiveLayoutView('CHAT')
        return
      }
      if (parts[2] === 'calendar') {
        setActiveLayoutView('CALENDAR')
        return
      }
      if (parts[2] === 'for-you') {
        setActiveLayoutView('FOR_YOU')
        return
      }
      if (parts[2] === 'uploads' || parts[2] === 'upload') {
        window.history.replaceState(null, '', toConsolePath(`/workspace/${slug}/upload`))
        setActiveLayoutView('UPLOAD')
        return
      }
      if (parts[2] === 'link') {
        setActiveLayoutView('LINK')
        return
      }
      if (parts[2] === 'stock') {
        setActiveLayoutView('STOCK')
        return
      }
      if (parts[2] === 'clients') {
        setActiveLayoutView('CLIENTS')
        return
      }
      if (parts[2] === 'timesheets') {
        setActiveLayoutView('TIMESHEETS')
        return
      }
      if (parts[2] === 'budget') {
        setActiveLayoutView('BUDGET')
        return
      }
      if (parts[2] === 'quota') {
        setActiveLayoutView('QUOTA')
        return
      }
      if (parts[2] === 'teaching') {
        const eduTab = parseEduTabParam(window.location.search)
        if (eduTab) setPendingEduTab(eduTab)
        setActiveLayoutView('TEACHING')
        return
      }
      if (parts[2] === 'training') {
        const eduTab = parseEduTabParam(window.location.search)
        if (eduTab) setPendingEduTab(eduTab)
        setActiveLayoutView('TRAINING')
        return
      }
      const matchedOpsSector = findOpsSectorByRouteSegment(parts[2])
      if (matchedOpsSector) {
        const opsTab = parseOpsTabParam(window.location.search, matchedOpsSector)
        if (opsTab) setPendingOpsTab(opsTab)
        setActiveLayoutView(OPS_SECTORS[matchedOpsSector].view)
        return
      }
      if (parts[2] === 'forms') {
        setActiveLayoutView('FORMS')
        return
      }
      if (parts[2] === 'meetings') {
        setActiveLayoutView('MEETINGS')
        return
      }
      if (parts[2] === 'automate') {
        setActiveLayoutView('AUTOMATE')
        return
      }
      if (parts[2] === 'design') {
        setActiveLayoutView('DESIGN')
        return
      }
      if (parts[2] === 'overview') {
        setActiveLayoutView('OVERVIEW')
        return
      }
      if (parts[2] === 'analysis') {
        setActiveLayoutView('ANALYSIS')
        return
      }
      if (parts[2] === 'manage' && parts[3] === 'audit') {
        setActiveLayoutView('SUMMARY')
        return
      }

      const nextBoard = parseBoardCode(parts[2])
      if (nextBoard) setActiveBoard(nextBoard)
      if (parts[3] === 'settings') {
        setWorkspaceSettingsInitialSection('OVERVIEW')
        setActiveLayoutView('WORKSPACE_SETTINGS')
        if (parts[4] === 'admin') setIsAdminPanelOpen(true)
        else setIsMemberPanelOpen(true)
        return
      }
      if (parts[3] === 'ai') {
        setActiveLayoutView('AI_MAGIC')
        return
      }
      if (parts[3] === 'manage') {
        const requestedManageMode = readManageModeFromSearch()
        if (requestedManageMode === 'board') {
          seedDetailedViewForManage()
          setManageMode('board')
          setActiveLayoutView('MANAGE')
          return
        }
        openManageDrawer()
        return
      }
      if (parts[3] === 'raid') {
        setActiveLayoutView('RAID')
        return
      }
      if (parts[3] === 'summary') {
        setActiveLayoutView('SUMMARY')
        return
      }
      if (parts[3] === 'reports') {
        setActiveLayoutView('REPORTS')
        return
      }
      if (parts[3] === 'checklists') {
        setActiveLayoutView('CHECKLISTS')
        maximizeCanvasWorkspace(WORKSPACE_DEFAULT_CHECKLIST_ID)
        return
      }
      if (nextBoard && nextBoard !== 'DASHBOARD' && !parts[3]) {
        seedDetailedViewForManage()
        setManageMode('board')
        setActiveLayoutView('MANAGE')
        return
      }

      setActiveLayoutView('DASHBOARD')

      const routePath = parts.slice(3).join('/')
      if (!routePath) {
        jumpToLevel('dashboard')
        return
      }
      const element = timelineElementByRoutePath.get(routePath)
      if (!element) {
        setRouteNotFoundPath(window.location.pathname)
        jumpToLevel('dashboard')
        return
      }
      setSelectedElementId(element.id)
      if (element.type === 'GOAL') jumpToLevel('goal', { goalId: element.id })
      if (element.type === 'TASK' && element.parentId) {
        jumpToLevel('task', { goalId: element.parentId, taskId: element.id })
      }
      if (element.type === 'SUBTASK' && element.parentId) {
        const parentTask = allTimelineElementById.get(element.parentId)
        if (parentTask?.parentId) {
          jumpToLevel('subtask', { goalId: parentTask.parentId, taskId: parentTask.id, subTaskId: element.id })
        }
      }
    }

    applyPathRoute()
    window.addEventListener('popstate', applyPathRoute)
    return () => window.removeEventListener('popstate', applyPathRoute)
  }, [activeWorkspaceId, allTimelineElementById, appMode, jumpToLevel, maximizeCanvasWorkspace, personalWorkspace, routeBase, setAppMode, setIsAdminPanelOpen, setIsMemberPanelOpen, setSelectedElementId, timelineElementByRoutePath, workspaces])

  useEffect(() => {
    const nextRoute = routeFromNavigation()
    const currentRoute = nextRoute.includes('?')
      ? `${window.location.pathname}${window.location.search}`
      : window.location.pathname
    if (currentRoute !== nextRoute) window.history.pushState(null, '', nextRoute)
  }, [routeFromNavigation])

  // Safety net: if a stale activeBoard (Visionboard / Archive / Bin) survives
  // a team→personal transition — e.g. a deep link, a Firestore listener race,
  // or a code path that forgot to reset it — collapse it back to DASHBOARD so
  // BoardExplorer doesn't render against a personal workspace that can't
  // service it.
  useEffect(() => {
    if (appMode === 'PERSONAL' && activeBoard !== 'DASHBOARD') {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Safety net resets stale team board to DASHBOARD on personal workspace transition
      setActiveBoard('DASHBOARD')
    }
  }, [appMode, activeBoard])

  useEffect(() => {
    setContextBoard(activeBoard)
  }, [activeBoard, setContextBoard])

  useEffect(() => {
    const activeIds = new Set([
      activeGoalId,
      activeTaskId,
      activeSubTaskId,
      selectedElementId,
      activeCanvasNodeId,
    ].filter((value): value is string => Boolean(value)))
    if (activeIds.size === 0) return
    notifications
      .filter((notification) => !notification.readAt && notification.entityId && activeIds.has(notification.entityId))
      .forEach((notification) => {
        void markRead(notification.id)
      })
  }, [activeCanvasNodeId, activeGoalId, activeSubTaskId, activeTaskId, markRead, notifications, selectedElementId])

  const selectedTimelineElement = selectedElementId
    ? timelineElements.find((element) => element.id === selectedElementId && (element.board ?? 'DASHBOARD') === 'DASHBOARD') ?? null
    : null
  const dashboardTimelineElements = useMemo(
    () => timelineElements.filter((element) => (element.board ?? 'DASHBOARD') === 'DASHBOARD'),
    [timelineElements],
  )
  const timelineElementById = useMemo(
    () => new Map(dashboardTimelineElements.map((element) => [element.id, element])),
    [dashboardTimelineElements],
  )

  const routeToTimelineElement = useCallback((notification: NotificationRecord) => {
    if (notification.workspaceId && notification.workspaceId !== activeWorkspaceId) {
      const targetWorkspace = workspaces.find((workspace) => workspace.id === notification.workspaceId)
      if (!targetWorkspace) return
      if (notification.entityType === 'CHECKLIST_ITEM' && notification.entityId) {
        const periodKey = typeof notification.data?.periodKey === 'string' && isPeriodKey(notification.data.periodKey)
          ? notification.data.periodKey
          : undefined
        pendingChecklistOpenRef.current = { itemId: notification.entityId, periodKey }
      }
      const link = notification.workspaceDeepLink ?? notification.deepLink ?? notification.deeplink
      const parsed = parseWorkspaceDeepLink(link)
      if (parsed) {
        void openWorkspaceTarget({
          workspaceId: notification.workspaceId,
          target: parsed.target,
          source: 'DEEPLINK',
          open: () => {
            window.history.pushState(null, '', link)
            window.dispatchEvent(new PopStateEvent('popstate'))
          },
          options: {
            activeWorkspaceId,
            workspaces,
            showToast,
            t,
            switchWorkspace: (id) => {
              const target = workspaces.find((w) => w.id === id)
              if (target) switchToWorkspace(target)
            },
          },
        })
        return
      }
      switchToWorkspace(targetWorkspace)
      if (link?.startsWith('/workspace/') || link?.startsWith('/w/')) {
        window.history.pushState(null, '', link)
      }
      return
    }
    if (notification.entityType === 'CHECKLIST_ITEM') {
      if (notification.entityId) {
        const periodKey = typeof notification.data?.periodKey === 'string' && isPeriodKey(notification.data.periodKey)
          ? notification.data.periodKey
          : undefined
        openChecklistDetail(notification.entityId, periodKey ? { initialOccurrenceKey: periodKey } : undefined)
      }
      return
    }

    const entityId = notification.entityId ?? ''
    if (!entityId) return

    const element = timelineElementById.get(entityId)
    setActiveLayoutView('DASHBOARD')
    setActiveBoard('DASHBOARD')
    setIsAdminPanelOpen(false)
    setIsMemberPanelOpen(false)

    if (!element) {
      showToast(t('NLSItemNotAvailable'), 'warning')
      goToDashboard()
      return
    }

    setSelectedElementId(element.id)
    if (element.type === 'GOAL') {
      jumpToLevel('goal', { goalId: element.id })
    } else if (element.type === 'TASK' && element.parentId) {
      jumpToLevel('task', { goalId: element.parentId, taskId: element.id })
    } else if (element.type === 'SUBTASK' && element.parentId) {
      const parentTask = timelineElementById.get(element.parentId)
      if (parentTask?.parentId) {
        jumpToLevel('subtask', { goalId: parentTask.parentId, taskId: parentTask.id, subTaskId: element.id })
      }
    }
  }, [
    goToDashboard,
    jumpToLevel,
    activeWorkspaceId,
    openChecklistDetail,
    setIsAdminPanelOpen,
    setIsMemberPanelOpen,
    setSelectedElementId,
    showToast,
    switchToWorkspace,
    t,
    timelineElementById,
    workspaces,
  ])

  useEffect(() => {
    const extractNotification = (event: Event) =>
      (event as CustomEvent<{ notification?: NotificationRecord }>).detail?.notification ?? null
    const guardChecklistExit = (action: () => void) => {
      if (activeLayoutView !== 'CHECKLISTS') {
        action()
        return
      }
      window.dispatchEvent(new CustomEvent('timelining:checklist-request-close', {
        detail: { onApproved: action },
      }))
    }

    const openFromNotification = (event: Event) => {
      const notification = extractNotification(event)
      if (!notification) return
      if (notification.entityType === 'CHECKLIST_ITEM') {
        routeToTimelineElement(notification)
        return
      }
      const deepLink = notification.workspaceDeepLink ?? notification.deepLink ?? notification.deeplink
      if (deepLink && deepLink.startsWith('/') && deepLink !== '/') {
        const parsed = parseWorkspaceDeepLink(deepLink)
        if (parsed) {
          guardChecklistExit(() => {
            void openWorkspaceTarget({
              workspaceId: parsed.workspaceId,
              target: parsed.target,
              source: 'DEEPLINK',
              open: () => {
                window.history.pushState(null, '', deepLink)
                window.dispatchEvent(new PopStateEvent('popstate'))
              },
              options: {
                activeWorkspaceId,
                workspaces,
                showToast,
                t,
                switchWorkspace: (id) => {
                  const target = workspaces.find((w) => w.id === id)
                  if (target) switchToWorkspace(target)
                },
              },
            })
          })
          return
        }
        guardChecklistExit(() => {
          window.history.pushState(null, '', deepLink)
          window.dispatchEvent(new PopStateEvent('popstate'))
        })
        return
      }
      if (
        notification.entityType === 'GOAL' ||
        notification.entityType === 'TASK' ||
        notification.entityType === 'SUBTASK'
      ) {
        guardChecklistExit(() => routeToTimelineElement(notification))
        return
      }
      if (notification.eventType === 'MENTION_CREATED') {
        guardChecklistExit(() => openChatPage(notification.workspaceId))
        return
      }
      if (notification.eventType === 'INVITE_RECEIVED') guardChecklistExit(openWorkspaceSettings)
    }

    const reviewInvite = (event: Event) => {
      const notification = extractNotification(event)
      const deepLink = notification?.workspaceDeepLink ?? notification?.deepLink ?? notification?.deeplink
      if (deepLink && deepLink.startsWith('/') && deepLink !== '/') {
        guardChecklistExit(() => {
          window.history.pushState(null, '', deepLink)
          window.dispatchEvent(new PopStateEvent('popstate'))
        })
        return
      }
      guardChecklistExit(openWorkspaceSettings)
    }
    const topUpCredits = () => guardChecklistExit(() => {
      setActiveLayoutView('PURCHASE')
      setIsAdminPanelOpen(false)
      setIsMemberPanelOpen(false)
    })
    const joinRaid = () => guardChecklistExit(() => {
      if (switchedOffModules.has('funPack')) {
        notifyModuleOff('funPack')
        return
      }
      setActiveBoard('DASHBOARD')
      setActiveLayoutView('RAID')
    })
    const openChat = (event: Event) => {
      const notification = extractNotification(event)
      guardChecklistExit(() => openChatPage(notification?.workspaceId))
    }

    window.addEventListener('timelining:notification-open', openFromNotification)
    window.addEventListener('timelining:notification-review-invite', reviewInvite)
    window.addEventListener('timelining:notification-top-up', topUpCredits)
    window.addEventListener('timelining:notification-join-raid', joinRaid)
    window.addEventListener('timelining:notification-open-chat', openChat)
    return () => {
      window.removeEventListener('timelining:notification-open', openFromNotification)
      window.removeEventListener('timelining:notification-review-invite', reviewInvite)
      window.removeEventListener('timelining:notification-top-up', topUpCredits)
      window.removeEventListener('timelining:notification-join-raid', joinRaid)
      window.removeEventListener('timelining:notification-open-chat', openChat)
    }
  }, [activeLayoutView, openChatPage, openWorkspaceSettings, routeToTimelineElement, setIsAdminPanelOpen, setIsMemberPanelOpen, switchedOffModules])

  const activeGoalElement = activeGoalId ? timelineElementById.get(activeGoalId) ?? null : null
  const activeTaskElement = activeTaskId ? timelineElementById.get(activeTaskId) ?? null : null
  const activeSubTaskElement = activeSubTaskId ? timelineElementById.get(activeSubTaskId) ?? null : null
  const selectedDetailsItem =
    currentLevel === 'goal'
      ? activeGoalElement
      : currentLevel === 'task'
        ? activeTaskElement
        : currentLevel === 'subtask'
          ? activeSubTaskElement
          : null
  const watchTargetItem = selectedTimelineElement ?? selectedDetailsItem

  useEffect(() => {
    if (!user || !watchTargetItem) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Resets watched item state when target item is cleared
      setIsWatchingActiveItem(false)
      return
    }
    if (isNoSignInPreviewActive()) {
      setIsWatchingActiveItem(false)
      return
    }
    const unsubscribe = onSnapshot(
      doc(firestoreDb, 'users', user.uid, 'watched_items', watchTargetItem.id),
      (snapshot) => setIsWatchingActiveItem(snapshot.exists()),
      () => setIsWatchingActiveItem(false),
    )
    return () => unsubscribe()
  }, [user, watchTargetItem])

  const toggleWatchActiveItem = async () => {
    if (!user || !activeWorkspaceId || !watchTargetItem) return
    const watchRef = doc(firestoreDb, 'users', user.uid, 'watched_items', watchTargetItem.id)
    if (isWatchingActiveItem) {
      await deleteDoc(watchRef)
      return
    }
    await setDoc(watchRef, {
      userId: user.uid,
      workspaceId: activeWorkspaceId,
      entityId: watchTargetItem.id,
      entityType: watchTargetItem.type,
      entityTitle: watchTargetItem.title,
      createdAt: serverTimestamp(),
    }, { merge: true })
  }
  const workspaceChecklistItem = useMemo<TimelineElement | null>(() => {
    if (!activeWorkspaceId) return null
    return {
      ...createStripboardRecordDefaults(),
      id: WORKSPACE_DEFAULT_CHECKLIST_ID,
      type: 'GOAL',
      title: 'Workspace Default Checklist',
      description: 'Standalone checklist storage for this workspace.',
      weight: 1,
      progress: 0,
      parentId: null,
      board: 'DASHBOARD',
      workspaceId: activeWorkspaceId,
      checklist: [],
      readAccess: [],
      writeAccess: [],
      directReadAccess: [],
      directWriteAccess: [],
    }
  }, [activeWorkspaceId])
  const activeCanvasItem = activeCanvasNodeId === WORKSPACE_DEFAULT_CHECKLIST_ID
    ? workspaceChecklistItem
    : activeCanvasNodeId
      ? timelineElementById.get(activeCanvasNodeId) ?? selectedDetailsItem ?? workspaceChecklistItem
      : selectedDetailsItem ?? workspaceChecklistItem

  // Two-way breadcrumb sync: when the dashboard's navigation level changes
  // while the canvas is maximized, follow it. Clicks in the canvas crumb
  // already call goToGoal/goToTask/goToSubTask (the other direction); this
  // closes the loop so navigating from the dashboard crumb doesn't leave
  // the canvas pinned to a stale node.
  useEffect(() => {
    if (!isCanvasMaximized) return
    const dashboardTargetId: string | null =
      currentLevel === 'subtask' ? activeSubTaskId
      : currentLevel === 'task' ? activeTaskId
      : currentLevel === 'goal' ? activeGoalId
      : WORKSPACE_DEFAULT_CHECKLIST_ID
    if (dashboardTargetId && dashboardTargetId !== activeCanvasNodeId) {
      setActiveCanvasNodeId(dashboardTargetId)
    }
  }, [
    isCanvasMaximized,
    currentLevel,
    activeGoalId,
    activeTaskId,
    activeSubTaskId,
    activeCanvasNodeId,
    setActiveCanvasNodeId,
  ])
  const canWriteSelectedElement =
    !selectedTimelineElement ||
    canEditElement(selectedTimelineElement)
  const canOpenEditTools = canMutate && canWriteSelectedElement
  const timelineGoalElements = dashboardTimelineElements.filter((element) => element.type === 'GOAL')
  const timelineStartDates = dashboardTimelineElements
    .map((element) => element.startDate ? new Date(element.startDate).getTime() : Number.NaN)
    .filter(Number.isFinite)
  const timelineEndDates = dashboardTimelineElements
    .map((element) => element.endDate ? new Date(element.endDate).getTime() : Number.NaN)
    .filter(Number.isFinite)
  const timelineStartDate = timelineStartDates.length > 0 ? new Date(Math.min(...timelineStartDates)) : null
  const timelineEndDate = timelineEndDates.length > 0 ? new Date(Math.max(...timelineEndDates)) : null
  const hasTimelineContent = timelineGoalElements.length > 0
  const userLabel = useDisplayName(userProfile ?? user)
  const userInitial = userLabel.trim().charAt(0).toUpperCase() || 'U'
  const userProfileImageUrl = resolveProfileImageUrl(userProfile, user)
  const bounds = getTimelineBounds(goals)
  const totalDays = bounds
    ? Math.round(getDurationInDays(bounds.startDate.getTime(), bounds.endDate.getTime()))
    : 0
  const railSummary = hasTimelineContent
    ? {
        totalDays: timelineStartDate && timelineEndDate
          ? Math.round(getDurationInDays(timelineStartDate.getTime(), timelineEndDate.getTime()))
          : totalDays,
        rangeLabel: timelineStartDate && timelineEndDate
          ? formatRange({ startDate: timelineStartDate, endDate: timelineEndDate })
          : bounds ? formatRange(bounds) : 'No bounds',
      }
    : null
  const displayedGlobalPersonalCredits = user?.email ? globalPersonalCredits : 0

  const openDashboardMoveManage = useCallback((movedElementId?: string) => {
    window.setTimeout(() => {
      const dashboardElements = timelineElements.filter((element) => (element.board ?? 'DASHBOARD') === 'DASHBOARD')
      const dashboardGoals = buildGoalsFromTimelineElements(
        dashboardElements,
        timelineChecklistItems,
      )
      setActiveBoard('DASHBOARD')
      setDetailedViewWorkingGoals(dashboardGoals)
      setDetailedViewBaselineGoals(dashboardGoals)
      setDetailedViewLiveScope('DASHBOARD')
      setDetailedViewWorkingGoal(null)
      setDetailedViewBaseline(null)
      const fallbackFocus: DetailedSelection | null = dashboardGoals[0]
        ? { kind: 'goal', goalId: dashboardGoals[0].id }
        : null
      const focus: DetailedSelection | null = movedElementId
        ? (elementIdToDetailedSelection(movedElementId, dashboardElements)
          ?? findSelectionForNodeId(movedElementId, undefined, dashboardGoals)
          ?? fallbackFocus)
        : fallbackFocus
      setDetailedViewInitialFocus(focus)
      if (focus) {
        const activeId = selectionElementId(focus)
        if (activeId) {
          setManageActiveElementId(activeId)
          setManageActiveWasExpanded(true)
        }
      }
      setDetailedViewMode('workspace')
      setDetailedViewChrome('manage')
      setManageMode('review')
      const nextPath = buildManageRoute(routeBase, appMode, 'DASHBOARD', 'review')
      if (nextPath && `${window.location.pathname}${window.location.search}` !== nextPath) {
        window.history.pushState(null, '', nextPath)
      }
      setActiveLayoutView('MANAGE')
    }, 350)
  }, [appMode, routeBase, timelineElements, timelineChecklistItems, setManageMode])

  useEffect(() => {
    if (!user?.email) return
    if (isNoSignInPreviewActive()) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Clears pending invites in no-sign-in preview mode
      setPendingInvites([])
      return
    }

    const invitesQuery = query(
      collection(firestoreDb, 'workspace_members'),
      where('user_email', '==', user.email.trim().toLowerCase()),
      where('status', '==', 'PENDING'),
    )
    const unsubscribe = onSnapshot(
      invitesQuery,
      (snapshot) => {
        setPendingInvites(snapshot.docs.map((snapshotDocument) => {
          const data = snapshotDocument.data()
          return {
            id: snapshotDocument.id,
            email: typeof data.user_email === 'string' ? data.user_email : '',
            workspaceId: typeof data.workspaceId === 'string' ? data.workspaceId : '',
            role: parseNonHelperRole(data.role) ?? 'VIEWER',
          } satisfies PendingInvite
        }))
      },
      (error) => {
        console.warn('[Firestore listener failed] App pending workspace_members invites by user_email/status', error)
        setPendingInvites([])
      },
    )

    return unsubscribe
  }, [user?.email])

  useEffect(() => {
    if (!activeWorkspaceId || activeWorkspaceIsPersonal || !roleCanPerform(activeRole, 'EDITOR')) return
    if (isNoSignInPreviewActive()) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Clears join requests in no-sign-in preview mode
      setIncomingJoinRequests([])
      return
    }

    const requestsQuery = query(
      collection(firestoreDb, 'join_requests'),
      where('workspaceId', '==', activeWorkspaceId),
      where('status', '==', 'PENDING'),
    )
    const unsubscribe = onSnapshot(
      requestsQuery,
      (snapshot) => {
        setIncomingJoinRequests(snapshot.docs.map((requestDocument) => {
          const data = requestDocument.data()
          return {
            id: requestDocument.id,
            workspaceId: typeof data.workspaceId === 'string' ? data.workspaceId : '',
            applicantEmail: typeof data.applicantEmail === 'string' ? data.applicantEmail : '',
            applicantUid: typeof data.applicantUid === 'string' ? data.applicantUid : '',
          } satisfies IncomingJoinRequest
        }))
      },
      (error) => {
        console.warn('[Firestore listener failed] App incoming join_requests by workspace/status', error)
        setIncomingJoinRequests([])
      },
    )

    return unsubscribe
  }, [activeRole, activeWorkspaceIsPersonal, activeWorkspaceId])

  const openCreateWorkspaceOnboarding = () => {
    createWorkspaceOnboarding.open()
    setIsWorkspaceMenuOpen(false)
    setIsProfileMenuOpen(false)
  }

  // Centralised reset for transient panels (chat, AI, manage, raid, workspace
  // settings) so rail navigation enforces "one screen at a time".
  const resetAllPanels = () => {
    closeDetailedView()
    closeCanvasWorkspace()
    closeWorkspacePanels()
  }

  const pathForPage = (view: ActiveLayoutView) => {
    if (view === 'EXPLORE') return toConsolePath('/explore')
    if (view === 'USER_SETTINGS') return toConsolePath('/settings')
    if (view === 'YOUR_PAGE') return toConsolePath('/your')
    if (view === 'PURCHASE') return toConsolePath('/purchase')
    if (view === 'INVITES') return toConsolePath('/team')
    if (view === 'FOCUS') return toConsolePath('/focus')
    if (view === 'LINK') return routeBase ? toConsolePath(`${routeBase}/link`) : null
    if (view === 'STOCK') return routeBase ? toConsolePath(`${routeBase}/stock`) : null
    if (view === 'CLIENTS') return routeBase ? toConsolePath(`${routeBase}/clients`) : null
    if (view === 'TIMESHEETS') return routeBase ? toConsolePath(`${routeBase}/timesheets`) : null
    if (view === 'BUDGET') return routeBase ? toConsolePath(`${routeBase}/budget`) : null
    if (view === 'QUOTA') return routeBase ? toConsolePath(`${routeBase}/quota`) : null
    if (view === 'TEACHING') return routeBase ? toConsolePath(`${routeBase}/teaching`) : null
    if (view === 'TRAINING') return routeBase ? toConsolePath(`${routeBase}/training`) : null
    const opsSectorForView = findOpsSectorByView(view)
    if (opsSectorForView) return routeBase ? toConsolePath(`${routeBase}/${OPS_SECTORS[opsSectorForView].routeSegment}`) : null
    if (view === 'UPLOAD') return routeBase ? toConsolePath(`${routeBase}/upload`) : null
    if (view === 'FORMS') return routeBase ? toConsolePath(`${routeBase}/forms`) : null
    if (view === 'MEETINGS') return routeBase ? toConsolePath(`${routeBase}/meetings`) : null
    if (view === 'AUTOMATE') return routeBase ? toConsolePath(`${routeBase}/automate`) : null
    if (view === 'DESIGN') return routeBase ? toConsolePath(`${routeBase}/design`) : null
    if (view === 'OVERVIEW') return routeBase ? toConsolePath(`${routeBase}/overview`) : null
    if (view === 'ANALYSIS') return routeBase ? toConsolePath(`${routeBase}/analysis`) : null
    if (!routeBase) return null

    const boardCode = BOARD_CODE_BY_KEY[appMode === 'PERSONAL' ? 'DASHBOARD' : activeBoard]
    const pageSegment: Partial<Record<ActiveLayoutView, string>> = {
      AI_MAGIC: 'ai',
      CHECKLISTS: 'checklists',
      RAID: 'raid',
      SUMMARY: 'summary',
      REPORTS: 'reports',
    }
    if (view === 'WORKSPACE_SETTINGS') {
      return appMode === 'PERSONAL'
        ? toConsolePath(`${routeBase}/settings`)
        : toConsolePath(`${routeBase}/${boardCode}/settings/${roleCanPerform(activeRole, 'ADMIN') ? 'admin' : 'member'}`)
    }
    if (view === 'SUMMARY') return appMode === 'TEAM' ? toConsolePath(`${routeBase}/manage/audit`) : null
    if (view === 'CHAT') return toConsolePath(`${routeBase}/chat`)
    if (view === 'CALENDAR') return toConsolePath(`${routeBase}/calendar`)
    if (view === 'FOR_YOU') return toConsolePath(`${routeBase}/for-you`)
    if (view === 'DASHBOARD') return toConsolePath(`${routeBase}/${boardCode}`)
    if (view === 'MANAGE') return toConsolePath(`${routeBase}/${boardCode}/manage?mode=${manageMode}`)
    return pageSegment[view] ? toConsolePath(`${routeBase}/${boardCode}/${pageSegment[view]}`) : null
  }

  const openPage = (view: ActiveLayoutView, queryString?: string) => {
    if (view === 'CHAT' && activeLayoutView !== 'CHAT') {
      preChatRouteRef.current = `${window.location.pathname}${window.location.search}${window.location.hash}`
    }
    resetAllPanels()
    if (view === 'PURCHASE') setPurchaseTab('TEAM')
    const basePath = pathForPage(view)
    const nextPath = basePath ? `${basePath}${queryString ? (queryString.startsWith('?') ? queryString : `?${queryString}`) : ''}` : null
    if (nextPath && (window.location.pathname !== basePath || (queryString && window.location.search !== queryString))) {
      window.history.pushState(
        view === 'CHAT' ? { ...window.history.state, chatReturnRoute: preChatRouteRef.current } : null,
        '',
        nextPath,
      )
    }
    setActiveLayoutView(view)
    if (view === 'REPORTS') {
      setReportRoute({ page: 'HUB', reportId: null })
      if (window.location.hash !== '#/reports') window.location.hash = '/reports'
    } else if (window.location.hash.startsWith('#/reports')) {
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`)
    }
  }

  const openHelperInvite = () => {
    openPage('INVITES', '?invite=helper')
  }

  const openUserSettings = (section: SettingsSection = 'GENERAL') => {
    setUserSettingsInitialSection(section)
    openPage('USER_SETTINGS')
  }

  // Bridge for globally-mounted debug UI (DebugModeIndicator) which lives outside
  // App.tsx's state tree. Start-capture navigates the user to the dashboard so
  // they can reproduce the issue; end-capture returns them to Support with the
  // session ready to attach. Events are dispatched on window so no context is
  // required in the debug component. Refs keep the handlers current without
  // re-registering listeners every render.
  const debugNavRef = useRef<{ openDashboard: () => void; openSupport: () => void }>({
    openDashboard: () => openPage('DASHBOARD'),
    openSupport: () => openUserSettings('SUPPORT'),
  })
  debugNavRef.current.openDashboard = () => openPage('DASHBOARD')
  debugNavRef.current.openSupport = () => openUserSettings('SUPPORT')
  useEffect(() => {
    const openDashboard = () => debugNavRef.current.openDashboard()
    const openSupport = () => debugNavRef.current.openSupport()
    window.addEventListener('timelining:openDashboard', openDashboard)
    window.addEventListener('timelining:openUserSettingsSupport', openSupport)
    return () => {
      window.removeEventListener('timelining:openDashboard', openDashboard)
      window.removeEventListener('timelining:openUserSettingsSupport', openSupport)
    }
  }, [])

  const openWorkspaceSettingsSection = (section: WorkspaceSettingsSection) => {
    setWorkspaceSettingsInitialSection(section)
    openPage('WORKSPACE_SETTINGS')
    openWorkspaceSettings()
  }

  const handlePreviewLockedItem = (target: { kind: 'THEME' | 'BACKGROUND' | 'SOUNDSCAPE'; cosmeticId: string }) => {
    setPendingCharacterHighlight(target)
    openPage('YOUR_PAGE')
  }

  const openExploreCategory = (categorySlug: string) => {
    resetAllPanels()
    const nextPath = toConsolePath(`/explore/${categorySlug}`)
    if (window.location.pathname !== nextPath) window.history.pushState(null, '', nextPath)
    setActiveLayoutView('EXPLORE')
  }

  // Mark the document root with data-preview-active so global CSS can hide
  // disabled mutation controls (Create/Add buttons) when viewing a demo/public
  // workspace. Applied at the <html> level so it flows into portals too.
  useEffect(() => {
    const root = document.documentElement
    if (activeWorkspaceIsPreview) root.setAttribute('data-preview-active', 'true')
    else root.removeAttribute('data-preview-active')
  }, [activeWorkspaceIsPreview])

  // Route guard: in preview mode, redirect any surface not in the mask back
  // to DASHBOARD. Blocks user-scoped surfaces that require a real workspace
  // (EXPLORE / PURCHASE / USER_SETTINGS) — with an escape-hatch for EXPLORE
  // when the visitor has no real workspaces yet (graduation fallback).
  useEffect(() => {
    if (!activeWorkspaceIsPreview) return
    if (viewRequiresRealWorkspace(activeLayoutView)) {
      if (activeLayoutView === 'EXPLORE' && !hasRealWorkspaces) return
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Route guard resets forbidden surface in preview mode
      setActiveLayoutView('DASHBOARD')
      return
    }
    const NEVER_BLOCK: ReadonlySet<ActiveLayoutView> = new Set(['DASHBOARD', 'YOUR_PAGE', 'INVITES', 'FOCUS'])
    if (NEVER_BLOCK.has(activeLayoutView)) return
    const LAYOUT_TO_SURFACE: Partial<Record<ActiveLayoutView, WorkspaceSurfaceKey>> = {
      CALENDAR: 'CALENDAR', CHECKLISTS: 'CHECKLISTS', REPORTS: 'REPORTS', FORMS: 'FORMS',
      MEETINGS: 'MEETINGS', AUTOMATE: 'AUTOMATE', DESIGN: 'DESIGN', CHAT: 'CHAT',
      STOCK: 'STOCK', BUDGET: 'BUDGET', QUOTA: 'QUOTA', RAID: 'RAID', AI_MAGIC: 'AI_MAGIC',
      TEACHING: 'TEACHING', TRAINING: 'TRAINING',
      CONSTRUCTION: 'CONSTRUCTION', HOSPITAL: 'HOSPITAL', SITES: 'SITES', DELIVERY: 'DELIVERY', SHOPS: 'SHOPS', STAYS: 'STAYS', JOBS: 'JOBS', CLINICS: 'CLINICS', PLANT: 'PLANT', FITNESS: 'FITNESS',
      MANAGE: 'MANAGE', SUMMARY: 'SUMMARY', OVERVIEW: 'OVERVIEW',
    }
    const surface = LAYOUT_TO_SURFACE[activeLayoutView]
    if (!surface) return
    if (activeLayoutView === 'WORKSPACE_SETTINGS') {
      if (!activeWorkspacePreviewMask?.showAdminSettingsReadOnly) setActiveLayoutView('DASHBOARD')
      return
    }
    if (!isSurfaceAllowed(activeWorkspacePreviewMask, surface)) {
      setActiveLayoutView('DASHBOARD')
    }
  }, [activeLayoutView, activeWorkspaceIsPreview, activeWorkspacePreviewMask, hasRealWorkspaces])

  const notifyModuleOffRef = useRef<(key: ModuleKey) => void>(() => undefined)
  useEffect(() => {
    notifyModuleOffRef.current = (key: ModuleKey) => {
      const moduleName = t(MODULE_SETTING_TITLE_KEYS[`${key}Enabled`])
      const action = canPerform('workspace.edit_settings')
        ? {
            label: t('NLSWorkspaceModuleTurnOnAction'),
            run: () => {
              setWorkspaceSettingsInitialSection('MODULES')
              openPage('WORKSPACE_SETTINGS')
              openWorkspaceSettings()
            },
          }
        : undefined
      showToast(
        t('NLSWorkspaceModuleTurnedOffToast', { module: moduleName }),
        'info',
        action,
        'normal',
        { durationMs: MODULE_TOAST_DURATION_MS },
      )
    }
  }, [
    canPerform,
    openPage,
    openWorkspaceSettings,
    setWorkspaceSettingsInitialSection,
    showToast,
    t,
  ])

  const notifyModuleOff = useCallback((key: ModuleKey) => {
    notifyModuleOffRef.current(key)
  }, [])

  const isOwner = useMemo(
    () => Boolean(user?.uid && isWorkspaceOwner(activeWorkspace, user.uid)) || activeRole === 'OWNER',
    [activeWorkspace, user?.uid, activeRole],
  )

  useEffect(() => {
    if (!activeWorkspaceId) return
    return subscribePageVisibility(activeWorkspaceId, user?.uid, isOwner, activeRole)
  }, [activeWorkspaceId, user?.uid, isOwner, activeRole])

  const isPageHidden = usePageVisibility()

  useModuleRouteGuard({
    activeLayoutView,
    switchedOffModules,
    isPageHidden,
    onRedirect: () => setActiveLayoutView('DASHBOARD'),
    onNotify: (key) => notifyModuleOffRef.current(key),
    onNotifyHidden: () => {
      showToast(t('NLSControlNoticePageHidden'), 'info')
    },
  })

  // Graduation: when the viewer clicks "Switch to my workspace" (or a blocked
  // action toast), leave the preview and route to their first real team/personal
  // workspace. Sets the demo-first-run flag so we don't auto-mount again.
  useEffect(() => {
    const handleGraduate = () => {
      const realWorkspaces = workspaces.filter((workspace) => !workspace.isPreview)
      const nextWorkspace = realWorkspaces.find((workspace) => isTeamWorkspace(workspace))
        ?? realWorkspaces.find((workspace) => isPersonalWorkspace(workspace))
        ?? null
      try {
        window.localStorage.setItem('timelining-demo-first-run-completed', '1')
      } catch { /* ignore */ }
      if (nextWorkspace) {
        switchToWorkspace(nextWorkspace)
      } else {
        openPage('EXPLORE')
      }
    }
    window.addEventListener(PREVIEW_GRADUATION_EVENT, handleGraduate)
    return () => window.removeEventListener(PREVIEW_GRADUATION_EVENT, handleGraduate)
  }, [workspaces, switchToWorkspace])

  const closeChatHub = () => {
    const fallbackPath = typeof window.history.state?.chatReturnRoute === 'string'
      ? window.history.state.chatReturnRoute
      : preChatRouteRef.current ?? (routeBase
      ? toConsolePath(`${routeBase}/${BOARD_CODE_BY_KEY[appMode === 'PERSONAL' ? 'DASHBOARD' : activeBoard]}`)
      : '/console')
    preChatRouteRef.current = null
    window.history.replaceState(null, '', fallbackPath)
    window.dispatchEvent(new PopStateEvent('popstate'))
  }

  const openElementMention = (elementId: string) => {
    const element = allTimelineElementById.get(elementId)
    if (!element || !routeBase) return

    const returnRoute = toConsolePath(`${routeBase}/${BOARD_CODE_BY_KEY[element.board ?? 'DASHBOARD']}${element.routePath ? `/${element.routePath}` : ''}`)
    preChatRouteRef.current = returnRoute
    window.history.pushState({ ...window.history.state, chatReturnRoute: returnRoute }, '', toConsolePath(`${routeBase}/chat`))
    setActiveBoard(element.board ?? 'DASHBOARD')
    setSelectedElementId(element.id)
    if (element.type === 'GOAL') jumpToLevel('goal', { goalId: element.id })
    if (element.type === 'TASK' && element.parentId) {
      jumpToLevel('task', { goalId: element.parentId, taskId: element.id })
    }
    if (element.type === 'SUBTASK' && element.parentId) {
      const parentTask = allTimelineElementById.get(element.parentId)
      if (parentTask?.parentId) {
        jumpToLevel('subtask', { goalId: parentTask.parentId, taskId: parentTask.id, subTaskId: element.id })
      }
    }
  }

  useEffect(() => {
    const restoreChatFocus = () => {
      if (!window.location.pathname.endsWith('/chat')) return
      const returnRoute = typeof window.history.state?.chatReturnRoute === 'string'
        ? window.history.state.chatReturnRoute
        : null
      if (!returnRoute) return
      preChatRouteRef.current = returnRoute

      const parts = getConsoleParts(new URL(returnRoute, window.location.origin).pathname)
      const board = parseBoardCode(parts[2])
      if (!board) return
      setActiveBoard(board)
      const routePath = parts.slice(3).join('/')
      if (!routePath) {
        setSelectedElementId(null)
        goToDashboard()
        return
      }
      const element = timelineElements.find((candidate) => candidate.board === board && candidate.routePath === routePath)
      if (!element) return
      setSelectedElementId(element.id)
      if (element.type === 'GOAL') jumpToLevel('goal', { goalId: element.id })
      if (element.type === 'TASK' && element.parentId) jumpToLevel('task', { goalId: element.parentId, taskId: element.id })
      if (element.type === 'SUBTASK' && element.parentId) {
        const parentTask = allTimelineElementById.get(element.parentId)
        if (parentTask?.parentId) jumpToLevel('subtask', { goalId: parentTask.parentId, taskId: parentTask.id, subTaskId: element.id })
      }
    }
    window.addEventListener('popstate', restoreChatFocus)
    return () => window.removeEventListener('popstate', restoreChatFocus)
  }, [allTimelineElementById, goToDashboard, jumpToLevel, setSelectedElementId, timelineElements])

  const openStoragePurchase = () => {
    resetAllPanels()
    setPurchaseTab('STORAGE')
    window.history.pushState(null, '', toConsolePath('/purchase/storage'))
    setActiveLayoutView('PURCHASE')
  }

  const openMeetingChat = (meeting: MeetingRecord) => {
    const scheduledAt = meeting.scheduledAt instanceof Date
      ? meeting.scheduledAt.toISOString()
      : 'toDate' in meeting.scheduledAt
        ? meeting.scheduledAt.toDate().toISOString()
        : String(meeting.scheduledAt)
    setMeetingContext({
      id: meeting.id,
      title: meeting.title,
      scheduledAt,
      sourceProvider: meeting.sourceProvider,
      status: meeting.status,
      agendaMarkdown: meeting.agendaMarkdown ?? '',
    })
    openPage('CHAT')
  }

  const openMeetingDocuments = (meetingId: string) => {
    setUploadArtifactFilter({ artifactType: 'MEETING_DOCUMENT', artifactId: meetingId })
    openPage('UPLOAD')
  }

  const navigateReports = (route: string) => {
    setActiveLayoutView('REPORTS')
    window.location.hash = route
  }

  const navigateAnalysis = (target: string) => {
    if (target === 'REPORTS') openPage('REPORTS')
    else if (target === 'CHECKLISTS') {
      openPage('CHECKLISTS')
      maximizeCanvasWorkspace(WORKSPACE_DEFAULT_CHECKLIST_ID)
    } else if (target === 'MEETINGS') openPage('MEETINGS')
    else if (target === 'UPLOAD') openPage('UPLOAD')
    else if (target === 'LINK') openPage('LINK')
    else if (target === 'AUTOMATE') openPage('AUTOMATE')
    else if (target === 'AI_MAGIC') openPage('AI_MAGIC')
    else if (target === 'BUDGET') openPage('BUDGET')
    else if (target === 'STOCK') openPage('STOCK')
    else if (target === 'CLIENTS') openPage('CLIENTS')
    else if (target === 'TIMESHEETS') openPage('TIMESHEETS')
    else if (target === 'SUMMARY') openPage('SUMMARY')
    else openPage('DASHBOARD')
  }

  const requestRightRailNavigation = (action: () => void) => {
    const checklistRouteActive = window.location.pathname.endsWith('/checklists')
    const guard = selectRightRailNavigationGuard({
      activeLayoutView,
      reportRoutePage: reportRoute.page,
      checklistRouteActive,
      hasActiveCanvasItem: Boolean(activeCanvasItem),
    })
    if (guard) {
      const detail = { onApproved: action, handled: false }
      window.dispatchEvent(new CustomEvent(guard.eventName, { detail }))
      if (!guard.respectHandledFlag || detail.handled) return
    }
    action()
  }

  const getManagePath = (mode: ManageWorkspaceMode, board: TimelineBoardKey = activeBoard) => {
    return buildManageRoute(routeBase, appMode, board, mode)
  }

  /** Seed the DetailedView (Review) working+baseline state from the current
   * timeline. Shared by every path that opens Manage, so both Board and Review
   * are always ready to render inside the dual-mount stage — switching between
   * them is now a pure visibility toggle, no teardown. */
  const detailedViewLiveGoals = useMemo(() => {
    if (detailedViewMode !== 'workspace' || detailedViewChrome !== 'manage' || !detailedViewLiveScope) return null
    const scoped = detailedViewLiveScope === 'DASHBOARD'
      ? timelineElements.filter((element) => (element.board ?? 'DASHBOARD') === 'DASHBOARD')
      : timelineElements
    return buildGoalsFromTimelineElements(scoped, timelineChecklistItems)
  }, [detailedViewMode, detailedViewChrome, detailedViewLiveScope, timelineElements, timelineChecklistItems])

  const rebaseDetailedViewToLive = useCallback((goals: Goal[]) => {
    setDetailedViewWorkingGoals(goals.map((goal) => JSON.parse(JSON.stringify(goal)) as Goal))
    setDetailedViewBaselineGoals(goals.map((goal) => JSON.parse(JSON.stringify(goal)) as Goal))
  }, [])

  function seedDetailedViewForManage() {
    const liveGoals = buildGoalsFromTimelineElements(timelineElements, timelineChecklistItems)
    setDetailedViewWorkingGoals(liveGoals.map((goal) => JSON.parse(JSON.stringify(goal)) as Goal))
    setDetailedViewBaselineGoals(liveGoals.map((goal) => JSON.parse(JSON.stringify(goal)) as Goal))
    setDetailedViewLiveScope('ALL')

    let focus: DetailedSelection | null = null
    if (selectedTimelineElement) {
      const elementsById = new Map(timelineElements.map((element) => [element.id, element]))
      if (selectedTimelineElement.type === 'SUBTASK') {
        const parentTask = selectedTimelineElement.parentId
          ? elementsById.get(selectedTimelineElement.parentId) ?? null
          : null
        const parentGoalId = parentTask?.parentId ?? null
        if (parentGoalId && parentTask) {
          focus = {
            kind: 'subtask',
            goalId: parentGoalId,
            taskId: parentTask.id,
            subTaskId: selectedTimelineElement.id,
          }
        }
      } else if (selectedTimelineElement.type === 'TASK') {
        const parentGoalId = selectedTimelineElement.parentId ?? null
        if (parentGoalId) {
          focus = {
            kind: 'task',
            goalId: parentGoalId,
            taskId: selectedTimelineElement.id,
          }
        }
      } else if (selectedTimelineElement.type === 'GOAL') {
        focus = { kind: 'goal', goalId: selectedTimelineElement.id }
      }
    }
    setDetailedViewInitialFocus(focus)
    setDetailedViewWorkingGoal(null)
    setDetailedViewBaseline(null)
    setDetailedViewInitialIssues(null)
    setDetailedViewAiNote(null)
    setDetailedViewAiNewIds(null)
    setDetailedViewAiChangedIds(null)
    setDetailedViewAiConfidence(null)
    setDetailedViewOperationalProposal(null)
    setDetailedViewMode('workspace')
    setDetailedViewChrome('manage')
  }

  function openManageDrawer() {
    if (selectedTimelineElement && !canEditElement(selectedTimelineElement)) return
    seedDetailedViewForManage()
    setManageMode('review')
    const nextPath = getManagePath('review')
    if (nextPath && `${window.location.pathname}${window.location.search}` !== nextPath) {
      window.history.pushState(null, '', nextPath)
    }
    setActiveLayoutView('MANAGE')
  }

  const handleReviewSelectionChange = useCallback((sel: DetailedSelection | null, expanded: boolean) => {
    setManageActiveElementId(selectionElementId(sel))
    setManageActiveWasExpanded(expanded)
  }, [])
  const handleBoardActiveChange = useCallback((id: string | null, isDrilledIn: boolean) => {
    setManageActiveElementId(id)
    setManageActiveWasExpanded(isDrilledIn)
  }, [])

  const seedMirrorForSwitch = (nextMode: ManageWorkspaceMode) => {
    if (nextMode === 'board') {
      const mirror = computeMirrorForSwitch({
        activeId: manageActiveElementId,
        wasExpanded: manageActiveWasExpanded,
        direction: 'toBoard',
      })
      if (mirror.direction === 'toBoard') {
        setPendingBoardOpenId(mirror.pendingBoardOpenId)
        setPendingBoardSelectOnlyId(mirror.pendingBoardSelectOnlyId)
      }
    } else {
      const mirror = computeMirrorForSwitch({
        activeId: manageActiveElementId,
        wasExpanded: manageActiveWasExpanded,
        direction: 'toReview',
        elements: timelineElements,
      })
      if (mirror.direction === 'toReview') {
        setDetailedViewInitialFocus(mirror.detailedViewInitialFocus)
      }
    }
  }

  const attemptManageModeSwitch = (nextMode: ManageWorkspaceMode) => {
    // Selection mirror runs BEFORE the mode flip so the destination view
    // seeds its focus from the current active-element state. See Part B.
    seedMirrorForSwitch(nextMode)
    applyManageModeSwitch(nextMode, manageMode, setManageMode, getManagePath)
  }

  function handleManageBoardChange(nextBoard: TimelineBoardKey, nextMode: ManageWorkspaceMode = manageMode) {
    const safeBoard: TimelineBoardKey = appMode === 'PERSONAL' && nextBoard !== 'DASHBOARD' ? 'DASHBOARD' : nextBoard
    const nextRoute = buildManageRoute(routeBase, appMode, safeBoard, nextMode)
    if (nextRoute && `${window.location.pathname}${window.location.search}` !== nextRoute) {
      window.history.pushState(null, '', nextRoute)
    }
    setBoardOpenElementId(null)
    setActiveBoard(safeBoard)
    goToDashboard()
  }

  const requestRestrictedWorkspaceAccess = async () => {
    if (!user?.email || !restrictedWorkspaceRoute) return
    const normalizedEmail = user.email.trim().toLowerCase()
    await setDoc(
      doc(firestoreDb, 'join_requests', `${restrictedWorkspaceRoute.workspaceId}_${user.uid}`),
      {
        workspaceId: restrictedWorkspaceRoute.workspaceId,
        workspaceSlug: restrictedWorkspaceRoute.slug,
        applicantUid: user.uid,
        applicantEmail: normalizedEmail,
        applicantName: userLabel || normalizedEmail,
        status: 'PENDING',
        requestedAt: serverTimestamp(),
      },
      { merge: true },
    )
  }

  useEffect(() => {
    const focusTimelineElement = (element: TimelineElement | null) => {
      if (!element?.startDate || !element.endDate) return
      focusItem(element.id, {
        startDate: element.startDate,
        endDate: element.endDate,
      })
    }

    if (currentLevel === 'dashboard') {
      resetViewWindow()
      return
    }

    if (currentLevel === 'goal') {
      focusTimelineElement(activeGoalElement)
      return
    }

    if (currentLevel === 'task') {
      focusTimelineElement(activeTaskElement)
      return
    }

    if (currentLevel === 'subtask') {
      focusTimelineElement(activeSubTaskElement)
    }
  }, [activeGoalElement, activeSubTaskElement, activeTaskElement, currentLevel, focusItem, resetViewWindow])

  const screenGuideId = (() => {
    if (activeLayoutView === 'DASHBOARD') return 'desktop.dashboard'
    if (activeLayoutView === 'MANAGE' && manageMode === 'board') return 'desktop.boards'
    const screenIds: Partial<Record<ActiveLayoutView, string>> = {
      CALENDAR: 'desktop.calendar',
      EXPLORE: 'desktop.explore',
      USER_SETTINGS: 'desktop.user-settings',
      YOUR_PAGE: 'desktop.your-page',
      PURCHASE: 'desktop.purchase',
      INVITES: 'desktop.invites',
      LINK: 'desktop.link',
      STOCK: 'desktop.stock',
      BUDGET: 'desktop.budget',
      QUOTA: 'desktop.quota',
      TEACHING: 'desktop.teaching',
      TRAINING: 'desktop.training',
      CONSTRUCTION: 'desktop.construction',
      HOSPITAL: 'desktop.hospital',
      SITES: 'desktop.sites',
      DELIVERY: 'desktop.delivery',
      SHOPS: 'desktop.shops',
      STAYS: 'desktop.stays',
      JOBS: 'desktop.jobs',
      CLINICS: 'desktop.clinics',
      PLANT: 'desktop.plant',
      FITNESS: 'desktop.fitness',
      UPLOAD: 'desktop.upload',
      FORMS: 'desktop.forms',
      MEETINGS: 'desktop.meetings',
      AUTOMATE: 'desktop.automate',
      DESIGN: 'desktop.design',
      AI_MAGIC: 'desktop.ai-magic',
      CHAT: 'desktop.chat',
      CHECKLISTS: 'desktop.checklists',
      MANAGE: 'desktop.manage',
      RAID: 'desktop.raid',
      FOR_YOU: 'desktop.todos',
      WORKSPACE_SETTINGS: 'desktop.workspace-settings',
      SUMMARY: 'desktop.ledger',
      OVERVIEW: 'desktop.overview',
      ANALYSIS: 'desktop.analysis',
      REPORTS: 'desktop.reports',
    }
    return screenIds[activeLayoutView] ?? null
  })()



  const handleLeftWorkspace = async () => {
    if (!leavingWorkspace) return
    const leftId = leavingWorkspace.id
    setLeavingWorkspace(null)
    if (refreshWorkspaces) await refreshWorkspaces()
    const remaining = workspaces.filter((entry) => entry.id !== leftId && !entry.isPreview)
    if (remaining.length > 0) {
      void requestWorkspaceSwitch({
        toWorkspaceId: remaining[0].id,
        source: 'MEMBERSHIP_ENDED',
        landing: safeWorkspaceLanding(remaining[0], 'DASHBOARD'),
      })
    } else {
      void requestWorkspaceSwitch({
        toWorkspaceId: null,
        source: 'MEMBERSHIP_ENDED',
      })
    }
  }

  const renderWorkspaceMenuButton = (workspace: typeof workspaces[number]) => {
    const role = getWorkspaceRole(workspace.id)
    const isActive = workspace.id === activeWorkspaceId
    const canLeave = !isPersonalWorkspace(workspace) && !workspace.isPreview
    return (
      <div key={workspace.id} className="workspaceMenuItemRow flex items-center justify-between w-full">
        <Pressable
          className={isActive ? 'isSelected flex-1' : 'flex-1'}
          onClick={() => switchToWorkspace(workspace)}
        >
          <span>
            {workspace.name}
            <small>/{isPersonalWorkspace(workspace) ? 'personal' : workspace.slug}</small>
          </span>
          <em className="workspaceRoleTag">{workspaceRoleLabel(role, workspace.isPreview)}</em>
        </Pressable>
        {canLeave ? (
          <IconButton
            variant="tertiary"
            tone="danger"
            size="sm"
            className="p-1 mr-1 rounded text-[var(--muted)] hover:text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))] hover:bg-[color-mix(in_srgb,var(--danger)_10%,transparent)] transition shrink-0"
            title={t('NLSInvitesLeaveWorkspaceAction')}
            label={`${t('NLSInvitesLeaveWorkspaceAction')} - ${workspace.name}`}
            data-testid={`leave-workspace-${workspace.id}`}
            icon={<LogoutRoundedIcon fontSize="inherit" />}
            onClick={(event) => {
              event.stopPropagation()
              setIsWorkspaceMenuOpen(false)
              setLeavingWorkspace({ id: workspace.id, name: workspace.name })
            }}
          />
        ) : null}
      </div>
    )
  }

  const notificationRouterCtx: NotificationRouterCtx = {
    workspaceSlug: activeWorkspace?.slug ?? '',
    workspaces,
    openIncomingInvites: () => openPage('INVITES'),
    openMemberSettings: () => openPage('WORKSPACE_SETTINGS'),
    openProfile: () => openPage('YOUR_PAGE'),
    openOverview: () => openPage('OVERVIEW'),
    openBoardNode: (nodeId) => routeToTimelineElement({ entityId: nodeId, entityType: 'TASK' } as NotificationRecord),
    openChecklistItem: (itemId, periodKey) => {
      openChecklistDetail(itemId, periodKey ? { initialOccurrenceKey: periodKey } : undefined)
    },
    // TODO(notifications-phase-2-plus): route directly to the requested chat thread.
    openChatThread: (_threadId) => { void _threadId; openPage('CHAT') },
    // TODO(notifications-phase-2-plus): route directly to the requested meeting.
    openMeeting: (_meetingId) => {
      void _meetingId
      if (switchedOffModules.has('meetings')) {
        notifyModuleOff('meetings')
        return
      }
      openPage('MEETINGS')
    },
    // TODO(notifications-phase-2-plus): route directly to the requested form.
    openForm: (_formId) => {
      void _formId
      if (switchedOffModules.has('forms')) {
        notifyModuleOff('forms')
        return
      }
      openPage('FORMS')
    },
    // TODO(notifications-phase-2-plus): route directly to the requested raid.
    openRaid: (_raidId) => {
      void _raidId
      if (switchedOffModules.has('funPack')) {
        notifyModuleOff('funPack')
        return
      }
      openPage('RAID')
    },
    // TODO(notifications-phase-2-plus): route directly to the requested achievement.
    openAchievement: (_achievementId) => {
      void _achievementId
      if (switchedOffModules.has('funPack')) {
        notifyModuleOff('funPack')
        return
      }
      openPage('YOUR_PAGE')
    },
    openCreditsLedger: () => openPage('PURCHASE'),
    openReport: (reportId) => {
      if (switchedOffModules.has('reports')) {
        notifyModuleOff('reports')
        return
      }
      openPage('REPORTS')
      setReportRoute({ page: 'DETAIL', reportId })
    },
    // TODO(notifications-phase-2-plus): route directly to the requested AI job.
    openAiHistory: (_jobId) => { void _jobId; openPage('AI_MAGIC') },
    goto: (url) => {
      window.history.pushState(null, '', url)
      window.dispatchEvent(new PopStateEvent('popstate'))
    },
    openStockItem: (itemId) => {
      setSelectedStockItemId(itemId)
      openPage('STOCK')
    },
    openTeam: () => openPage('INVITES'),
    switchToWorkspaceForTarget: (targetId, open, wsName) =>
      switchToWorkspaceForTarget(targetId, open, {
        activeWorkspaceId,
        workspaces,
        workspaceName: wsName,
        switchWorkspace: (id) => {
          const target = workspaces.find((w) => w.id === id)
          if (target) switchToWorkspace(target)
        },
        showToast,
        t,
      }),
    openWorkspaceTarget: (args) =>
      openWorkspaceTarget({
        ...args,
        options: {
          activeWorkspaceId,
          workspaces,
          switchWorkspace: (id) => {
            const target = workspaces.find((w) => w.id === id)
            if (target) switchToWorkspace(target)
          },
          showToast,
          t,
          ...args.options,
        },
      }),
    t,
  }
  const notificationRouter: NotificationRouterHandle = useMemo(() => ({
    resolveNotification: (notification: NotificationRecord) => resolveNotification(notification, notificationRouterCtx),
    resolveDigestItem: (item: DigestInnerItem) => resolveDigestItem(item, notificationRouterCtx),
  }), [notificationRouterCtx])

  if (createWorkspaceOnboarding.gate) return createWorkspaceOnboarding.gate

  const canAccessClients = Boolean(
    activeWorkspace &&
      !activeWorkspaceIsPersonal &&
      appMode === 'TEAM' &&
      !switchedOffModules.has('clientOutreach') &&
      !isAccessRestricted &&
      activeRole &&
      roleCanPerform(activeRole, 'EDITOR'),
  )

  const canAccessTimesheets = Boolean(
    activeWorkspace &&
      !activeWorkspaceIsPersonal &&
      appMode === 'TEAM' &&
      !switchedOffModules.has('timesheets') &&
      isTimesheetsTierEligibleForWorkspace(activeWorkspace.plan, activeWorkspace),
  )

  const rightRailProps: RightSideNavProps = {
    userLabel,
    userInitial,
    userEmail: user?.email ?? null,
    userPhotoUrl: userProfileImageUrl || null,
    workspacePlan: activeWorkspace?.plan,
    summary: railSummary,
    personalWalletCredits: displayedGlobalPersonalCredits,
    notificationRouter,
    switchedOffModules,
    isPageHidden,
    onOpenOverview: appMode === 'TEAM' && canPerform('overview.view') ? () => requestRightRailNavigation(() => openPage('OVERVIEW')) : undefined,
    isOverviewOpen: activeLayoutView === 'OVERVIEW',
    isOverviewAvailable: appMode === 'TEAM' && canPerform('overview.view'),
    onOpenDashboard: () => requestRightRailNavigation(() => {
      preChatRouteRef.current = null
      // Sync URL before state — otherwise applyPathRoute (which re-runs
      // when its deps move, e.g. a Firestore element snapshot lands)
      // reads the stale board URL and yanks activeBoard back to the
      // previous board, flickering BoardExplorer in for a render
      // before settling on the dashboard.
      if (routeBase) {
        const nextRoute = toConsolePath(`${routeBase}/${BOARD_CODE_BY_KEY['DASHBOARD']}`)
        if (window.location.pathname !== nextRoute) {
          window.history.pushState(null, '', nextRoute)
        }
      }
      resetAllPanels()
      setActiveLayoutView('DASHBOARD')
      setActiveBoard('DASHBOARD')
      resetViewWindow()
      goToDashboard()
    }),
    isDashboardOpen: activeLayoutView === 'DASHBOARD',
    onOpenCalendar: () => requestRightRailNavigation(() => openPage('CALENDAR')),
    isCalendarAvailable: true,
    isCalendarOpen: activeLayoutView === 'CALENDAR',
    onOpenMeetings: canPerform('meetings.view') ? () => requestRightRailNavigation(() => openPage('MEETINGS')) : undefined,
    isMeetingsOpen: activeLayoutView === 'MEETINGS',
    onOpenForms: canPerform('forms.view') ? () => requestRightRailNavigation(() => openPage('FORMS')) : undefined,
    isFormsOpen: activeLayoutView === 'FORMS',
    onOpenAutomate: canPerform('automation.view') ? () => requestRightRailNavigation(() => openPage('AUTOMATE')) : undefined,
    isAutomateOpen: activeLayoutView === 'AUTOMATE',
    onOpenDesign: canPerform('forms.view') ? () => requestRightRailNavigation(() => openPage('DESIGN')) : undefined,
    isDesignOpen: activeLayoutView === 'DESIGN',
    onOpenChat: appMode === 'TEAM' && canPerform('chat.view') ? () => requestRightRailNavigation(() => openPage('CHAT')) : undefined,
    isChatAvailable: appMode === 'TEAM' && canPerform('chat.view'),
    isChatOpen: activeLayoutView === 'CHAT',
    onOpenRaid: appMode === 'TEAM' && canPerform('raid.participate') && !switchedOffModules.has('funPack') ? () => requestRightRailNavigation(() => openPage('RAID')) : undefined,
    isRaidActive: Boolean(activeRaid),
    isRaidPanelOpen: activeLayoutView === 'RAID',
    isRaidAvailable: appMode === 'TEAM' && canPerform('raid.participate') && !switchedOffModules.has('funPack'),
    onOpenForYou: appMode === 'TEAM' ? () => requestRightRailNavigation(() => openPage('FOR_YOU')) : undefined,
    isForYouOpen: activeLayoutView === 'FOR_YOU',
    isForYouAvailable: appMode === 'TEAM',
    onOpenFocus: () => requestRightRailNavigation(() => openPage('FOCUS')),
    isFocusOpen: activeLayoutView === 'FOCUS',
    onOpenSummary: roleCanPerform(activeRole, 'EDITOR') ? () => requestRightRailNavigation(() => openPage('SUMMARY')) : undefined,
    isSummaryOpen: activeLayoutView === 'SUMMARY',
    onOpenAnalysis: (canPerform('workspace.view_analysis') || canPerform('readonly.view_analysis')) && appMode === 'TEAM' ? () => requestRightRailNavigation(() => openPage('ANALYSIS')) : undefined,
    isAnalysisOpen: activeLayoutView === 'ANALYSIS',
    onOpenReports: canPerform('workspace.view_reports') && appMode === 'TEAM' ? () => requestRightRailNavigation(() => openPage('REPORTS')) : undefined,
    isReportsOpen: activeLayoutView === 'REPORTS',
    onOpenInvites: () => requestRightRailNavigation(() => openPage('INVITES')),
    isInvitesOpen: activeLayoutView === 'INVITES',
    onOpenLink: appMode === 'TEAM' && canPerform('integrations.view') ? () => requestRightRailNavigation(() => openPage('LINK')) : undefined,
    isLinkOpen: activeLayoutView === 'LINK',
    onOpenStock: canViewStockForWorkspace(activeWorkspace ?? null, activeRole) ? () => requestRightRailNavigation(() => openPage('STOCK')) : undefined,
    isStockOpen: activeLayoutView === 'STOCK',
    onOpenClients: canAccessClients ? () => requestRightRailNavigation(() => openPage('CLIENTS')) : undefined,
    isClientsOpen: activeLayoutView === 'CLIENTS',
    onOpenTimesheets: canAccessTimesheets ? () => requestRightRailNavigation(() => openPage('TIMESHEETS')) : undefined,
    isTimesheetsOpen: activeLayoutView === 'TIMESHEETS',
    onOpenBudget: isBudgetEnabledForWorkspace(activeWorkspace ?? null) ? () => requestRightRailNavigation(() => openPage('BUDGET')) : undefined,
    isBudgetOpen: activeLayoutView === 'BUDGET',
    onOpenQuota: isQuotaEnabledForWorkspace(activeWorkspace ?? null) ? () => requestRightRailNavigation(() => openPage('QUOTA')) : undefined,
    isQuotaOpen: activeLayoutView === 'QUOTA',
    onOpenTeaching: isTeachingEnabledForWorkspace(activeWorkspace ?? null) ? () => requestRightRailNavigation(() => openPage('TEACHING')) : undefined,
    isTeachingOpen: activeLayoutView === 'TEACHING',
    onOpenTraining: isTrainingEnabledForWorkspace(activeWorkspace ?? null) ? () => requestRightRailNavigation(() => openPage('TRAINING')) : undefined,
    isTrainingOpen: activeLayoutView === 'TRAINING',
    onOpenConstruction: isConstructionEnabledForWorkspace(activeWorkspace ?? null) ? () => requestRightRailNavigation(() => openPage('CONSTRUCTION')) : undefined,
    isConstructionOpen: activeLayoutView === 'CONSTRUCTION',
    onOpenHospital: isHospitalEnabledForWorkspace(activeWorkspace ?? null) ? () => requestRightRailNavigation(() => openPage('HOSPITAL')) : undefined,
    isHospitalOpen: activeLayoutView === 'HOSPITAL',
    onOpenSites: isSitesEnabledForWorkspace(activeWorkspace ?? null) ? () => requestRightRailNavigation(() => openPage('SITES')) : undefined,
    isSitesOpen: activeLayoutView === 'SITES',
    onOpenDelivery: isDeliveryEnabledForWorkspace(activeWorkspace ?? null) ? () => requestRightRailNavigation(() => openPage('DELIVERY')) : undefined,
    isDeliveryOpen: activeLayoutView === 'DELIVERY',
    onOpenShops: isShopsEnabledForWorkspace(activeWorkspace ?? null) ? () => requestRightRailNavigation(() => openPage('SHOPS')) : undefined,
    isShopsOpen: activeLayoutView === 'SHOPS',
    onOpenStays: isStaysEnabledForWorkspace(activeWorkspace ?? null) ? () => requestRightRailNavigation(() => openPage('STAYS')) : undefined,
    isStaysOpen: activeLayoutView === 'STAYS',
    onOpenJobs: isJobsEnabledForWorkspace(activeWorkspace ?? null) ? () => requestRightRailNavigation(() => openPage('JOBS')) : undefined,
    isJobsOpen: activeLayoutView === 'JOBS',
    onOpenClinics: isClinicsEnabledForWorkspace(activeWorkspace ?? null) ? () => requestRightRailNavigation(() => openPage('CLINICS')) : undefined,
    isClinicsOpen: activeLayoutView === 'CLINICS',
    onOpenPlant: isPlantEnabledForWorkspace(activeWorkspace ?? null) ? () => requestRightRailNavigation(() => openPage('PLANT')) : undefined,
    isPlantOpen: activeLayoutView === 'PLANT',
    onOpenFitness: isFitnessEnabledForWorkspace(activeWorkspace ?? null) ? () => requestRightRailNavigation(() => openPage('FITNESS')) : undefined,
    isFitnessOpen: activeLayoutView === 'FITNESS',
    onOpenUpload: appMode === 'TEAM' && canPerform('integrations.view') ? () => requestRightRailNavigation(() => { setUploadArtifactFilter(null); openPage('UPLOAD') }) : undefined,
    isUploadOpen: activeLayoutView === 'UPLOAD',
    onOpenWorkspaceSettings: (activeRole !== 'READONLY' || activeWorkspace?.previewSurfaceMask?.showAdminSettingsReadOnly) ? () => requestRightRailNavigation(() => {
      setWorkspaceSettingsInitialSection('OVERVIEW')
      openPage('WORKSPACE_SETTINGS')
      openWorkspaceSettings()
    }) : undefined,
    isWorkspaceSettingsOpen: activeLayoutView === 'WORKSPACE_SETTINGS',
    onOpenAiMagic: canPerform('ai.use') ? () => requestRightRailNavigation(() => openPage('AI_MAGIC')) : undefined,
    isAiMagicOpen: activeLayoutView === 'AI_MAGIC',
    isAiMagicAvailable: canPerform('ai.use'),
    onOpenChecklists: () => requestRightRailNavigation(() => {
      openPage('CHECKLISTS')
      maximizeCanvasWorkspace(WORKSPACE_DEFAULT_CHECKLIST_ID)
    }),
    isChecklistsOpen: activeLayoutView === 'CHECKLISTS',
    onOpenManage: canOpenEditTools
      ? () => requestRightRailNavigation(() => {
          // Manage is a base page like every other rail page — no toggling,
          // no "close-to-restore-previous". Clicking when already in Manage
          // is a no-op; clicking otherwise swaps to Manage and lands on
          // the Review sub-view (default).
          if (activeLayoutView === 'MANAGE') return
          resetAllPanels()
          openManageDrawer()
        })
      : undefined,
    isManageOpen: activeLayoutView === 'MANAGE',
    isManageAvailable: canOpenEditTools,
    onOpenExplore: (activeWorkspaceIsPreview && hasRealWorkspaces)
      ? undefined
      : () => requestRightRailNavigation(() => openPage('EXPLORE')),
    isExploreOpen: activeLayoutView === 'EXPLORE',
    onOpenYourPage: () => requestRightRailNavigation(() => openPage('YOUR_PAGE')),
    onOpenYourDetails: () => requestRightRailNavigation(() => {
      openUserSettings('PROFILE')
    }),
    isYourPageOpen: activeLayoutView === 'YOUR_PAGE',
    onOpenPurchase: activeWorkspaceIsPreview
      ? undefined
      : () => requestRightRailNavigation(() => openPage('PURCHASE')),
    isPurchaseOpen: activeLayoutView === 'PURCHASE',
    onOpenUserSettings: activeWorkspaceIsPreview
      ? undefined
      : () => requestRightRailNavigation(() => openUserSettings()),
    isUserSettingsOpen: activeLayoutView === 'USER_SETTINGS',
    onOpenFamilyView: canSwitchToFamily(activeWorkspaceId) ? () => {
      const firstStaffChild = familyLearners.find((l) => l.workspaceId === activeWorkspaceId && l.isStaffHere)
      const childKey = firstStaffChild ? `${firstStaffChild.workspaceId}_${firstStaffChild.learnerId}` : ''
      const targetUrl = childKey ? `/family?child=${encodeURIComponent(childKey)}` : '/family'
      if (typeof window !== 'undefined') {
        window.history.replaceState({ fromWorkspaceId: activeWorkspaceId }, '', targetUrl)
        window.dispatchEvent(new PopStateEvent('popstate'))
      }
    } : undefined,
    onLogout: () => void signOutUser(),
  }

  return (
    <main className="appShell">
      <RaidCelebration workspaceId={appMode === 'TEAM' && !switchedOffModules.has('funPack') ? activeWorkspaceId : null} />
      {!isMobileViewport ? (
        <RightSideNav {...rightRailProps} />
      ) : (
        <RightSideNavMobile
          railProps={rightRailProps}
          activeLayoutView={activeLayoutView}
          activeDashboardView={activeDashboardView}
          dockEnabledViews={dockEnabledViews}
          onSelectDashboardView={(view) => {
            setActiveLayoutView('DASHBOARD')
            setActiveBoard('DASHBOARD')
            goToDashboard()
            setPendingDashboardView(view)
          }}
          onHeightChange={setMobileTabBarHeight}
        />
      )}
      <div
        className="appShellInner"
        style={isMobileViewport ? {
          paddingTop: mobileHeaderHeight > 0 ? `${mobileHeaderHeight}px` : undefined,
          paddingBottom: mobileTabBarHeight > 0 ? `${mobileTabBarHeight}px` : undefined,
        } : undefined}
      >
      {workspaceLoadingError ? (
        <div className="viewerBanner" data-preview-kind="PARTIAL_ERROR" role="alert">
          <div className="viewerBannerInner">
            <span className="viewerBannerPill" style={{ background: '#f59e0b', color: '#000', borderColor: '#d97706' }}>Notice</span>
            <span className="viewerBannerMessage">{workspaceLoadingError}</span>
            <div className="viewerBannerActions">
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => { void refreshWorkspaces() }}
              >
                Retry
              </Button>
            </div>
          </div>
        </div>
      ) : null}
      <ViewerBanner
        onTakeTour={() => {
          try { window.dispatchEvent(new CustomEvent('timelining:demo-take-tour')) } catch { /* ignore */ }
        }}
      />
      <PreviewExitIntent />
      <header className="appHeader" ref={headerRef}>
        <div className="headerLeft">
          {activeLayoutView === 'USER_SETTINGS' ? (
            <span className="personalSpaceLabel">User Settings</span>
          ) : activeLayoutView === 'EXPLORE' ? (
            <span className="personalSpaceLabel">Explore Hub</span>
          ) : activeLayoutView === 'YOUR_PAGE' ? (
            <span className="personalSpaceLabel">Your Page</span>
          ) : (
            <div className="headerMenuWrap" data-floating-menu-root="true">
              <Pressable
                ref={workspaceMenuButtonRef}
                className="workspaceMenuButton"
                data-tour="workspace-switcher"
                onClick={() => {
                  setIsWorkspaceMenuOpen((current) => !current)
                  setIsProfileMenuOpen(false)
                }}
                aria-expanded={isWorkspaceMenuOpen}
              >
                <span className="topPillMarker" aria-hidden="true">›</span>
                {activeWorkspace && (
                  <WorkspaceBrandChip
                    name={activeWorkspace.name}
                    logoUrl={activeWorkspace.branding?.logoUrl}
                    accentColor={activeWorkspace.branding?.accentColor}
                    fallback="none"
                    size={24}
                  />
                )}
                <span className="topPillLabel">{activeWorkspace?.name ?? 'No workspace selected'}</span>
                {activeWorkspace && (
                  <span className="topPillRole">{workspaceRoleLabel(getWorkspaceRole(activeWorkspace.id), activeWorkspace.isPreview)}</span>
                )}
              </Pressable>
              <FloatingMenu
                anchorRef={workspaceMenuButtonRef}
                isOpen={isWorkspaceMenuOpen}
                onClose={() => setIsWorkspaceMenuOpen(false)}
                placement="bottom-start"
                className="headerDropdown workspaceDropdown"
                role="menu"
                ariaLabel="Workspace menu"
              >
                  {workspaceMenuRows.length > 0 && (
                    <>
                      <div className="workspaceList">
                        {workspaceMenuRows.map(renderWorkspaceMenuButton)}
                      </div>
                    </>
                  )}
                  {workspaceMenuRows.length > 0 && (otherRealSwitchWorkspaces.length > 0 || otherPreviewSwitchWorkspaces.length > 0) && (
                    <hr className="workspaceDropdownDivider" />
                  )}
                  {otherRealSwitchWorkspaces.length > 0 && (
                    <>
                      <div className="workspaceList">
                        {otherRealSwitchWorkspaces.map(renderWorkspaceMenuButton)}
                      </div>
                    </>
                  )}
                  {activeWorkspaceIsPreview && otherRealSwitchWorkspaces.length > 0 && otherPreviewSwitchWorkspaces.length > 0 && (
                    <hr className="workspaceDropdownDivider" />
                  )}
                  {otherPreviewSwitchWorkspaces.length > 0 && (
                    <>
                      <div className="workspaceList">
                        {otherPreviewSwitchWorkspaces.map(renderWorkspaceMenuButton)}
                      </div>
                    </>
                  )}
                  <div className="workspaceDropdownActions">
                    <Button
                      variant="tertiary"
                      size="sm"
                      onClick={() => {
                        setIsWorkspaceMenuOpen(false)
                        openExploreCategory('workspaces')
                      }}
                    >
                      Explore workspaces
                    </Button>
                    {canCreateWorkspace && (
                      <Button
                        variant="tertiary"
                        size="sm"
                        onClick={() => openCreateWorkspaceOnboarding()}
                      >
                        Create workspace
                      </Button>
                    )}
                    {!personalWorkspace && user && (
                      <Button
                        variant="tertiary"
                        size="sm"
                        onClick={() => {
                          setIsWorkspaceMenuOpen(false)
                          if (activateExistingPersonal()) return
                          window.location.href = '/personal'
                        }}
                      >
                        {t('NLSWorkspaceMenuCreatePersonal')}
                      </Button>
                    )}
                  </div>
                  {!activeWorkspace && personalWorkspace && (
                    <>
                      <hr className="workspaceDropdownDivider" />
                      <div className="workspaceList">
                        {renderWorkspaceMenuButton(personalWorkspace)}
                      </div>
                    </>
                  )}
              </FloatingMenu>
              {leavingWorkspace ? (
                <LeaveWorkspaceConfirmModal
                  open={Boolean(leavingWorkspace)}
                  workspaceId={leavingWorkspace.id}
                  workspaceName={leavingWorkspace.name}
                  onClose={() => setLeavingWorkspace(null)}
                  onConfirm={async () => {
                    await leaveWorkspaceAndPurgeOutbox({ workspaceId: leavingWorkspace.id })
                  }}
                  onLeft={handleLeftWorkspace}
                  onNavigateToOwners={() => {
                    setLeavingWorkspace(null)
                    openPage('INVITES')
                  }}
                />
              ) : null}
            </div>
          )}
        </div>
        <div className="headerCenter">
          <Pressable
            className="brand"
            onClick={() => {
              if (activeLayoutView === 'CHAT') {
                closeChatHub()
                return
              }
              resetViewWindow()
              setActiveLayoutView('DASHBOARD')
              goToDashboard()
            }}
          >
            <img className="brandMark" src={timeliningLogo} alt="" aria-hidden="true" />
            <OverflowTip label="Timelining">
              <span className="brandTitle">Timelining</span>
            </OverflowTip>
          </Pressable>
        </div>
        <div className="navControls">
          <RunningTimerBar />
          <DebugModeIndicator />
          <ScreenGuideHost
            screenId={screenGuideId}
            disabled={Boolean(restrictedWorkspaceRoute) || isTourActive}
            className="screenGuideLauncher--topbar"
          />
        </div>
      </header>

      <Suspense fallback={<AppSurfaceFallback />}>
        {activeLayoutView === 'USER_SETTINGS' ? (
          <UserSettingsPage
            activeWorkspace={activeWorkspace}
            initialSection={userSettingsInitialSection}
            onBack={() => setActiveLayoutView('DASHBOARD')}
            onPreviewLockedItem={handlePreviewLockedItem}
          />
        ) : activeLayoutView === 'EXPLORE' ? (
          <ExploreHubPanel
            onBack={() => setActiveLayoutView('DASHBOARD')}
            visibleSections={['public', 'requests']}
            hideBack
            onOpenModuleSettings={canPerform('workspace.edit_settings') ? () => openWorkspaceSettingsSection('MODULES') : undefined}
          />
        ) : activeLayoutView === 'INVITES' ? (
          <InvitesPage onOpenWorkspaceSettings={() => openWorkspaceSettingsSection('MEMBERS')} />
        ) : activeLayoutView === 'LINK' && !switchedOffModules.has('linkHub') ? (
          <LinkHub onManagePlan={openStoragePurchase} />
        ) : activeLayoutView === 'STOCK' && !switchedOffModules.has('stock') ? (
          <StockHub onManagePlan={openStoragePurchase} initialItemId={selectedStockItemId} />
        ) : activeLayoutView === 'CLIENTS' && !switchedOffModules.has('clientOutreach') && canAccessClients ? (
          <ClientsPage onManagePlan={openStoragePurchase} />
        ) : activeLayoutView === 'TIMESHEETS' && !switchedOffModules.has('timesheets') ? (
          <TimesheetsHubLayout onManagePlan={openStoragePurchase} />
        ) : activeLayoutView === 'BUDGET' && !switchedOffModules.has('budget') ? (
          <BudgetHubLayout
            onManagePlan={openStoragePurchase}
            onOpenWorkspaceSettings={() => openWorkspaceSettingsSection('MODULES')}
          />
        ) : activeLayoutView === 'QUOTA' && !switchedOffModules.has('quota') ? (
          <QuotaHubLayout
            onManagePlan={openStoragePurchase}
            onOpenWorkspaceSettings={() => openWorkspaceSettingsSection('MODULES')}
          />
        ) : activeLayoutView === 'TEACHING' && !switchedOffModules.has('teaching') ? (
          <EducationHub
            kind="TEACHING"
            initialTab={pendingEduTab ?? undefined}
            onClearInitialTab={handleClearPendingEduTab}
          />
        ) : activeLayoutView === 'TRAINING' && !switchedOffModules.has('training') ? (
          <EducationHub
            kind="TRAINING"
            initialTab={pendingEduTab ?? undefined}
            onClearInitialTab={handleClearPendingEduTab}
          />
        ) : activeLayoutView === 'CONSTRUCTION' && !switchedOffModules.has('construction') ? (
          <ConstructionHub
            workspace={activeWorkspace}
            initialTab={pendingOpsTab ?? undefined}
            onClearInitialTab={handleClearPendingOpsTab}
          />
        ) : activeLayoutView === 'HOSPITAL' && !switchedOffModules.has('hospital') ? (
          <HospitalHub
            workspace={activeWorkspace}
            initialTab={pendingOpsTab ?? undefined}
            onClearInitialTab={handleClearPendingOpsTab}
          />
        ) : activeLayoutView === 'SITES' && !switchedOffModules.has('sites') ? (
          <SitesHub
            workspace={activeWorkspace}
            initialTab={pendingOpsTab ?? undefined}
            onClearInitialTab={handleClearPendingOpsTab}
          />
        ) : activeLayoutView === 'DELIVERY' && !switchedOffModules.has('delivery') ? (
          <DeliveryHub
            workspace={activeWorkspace}
            initialTab={pendingOpsTab ?? undefined}
            onClearInitialTab={handleClearPendingOpsTab}
          />
        ) : activeLayoutView === 'SHOPS' && !switchedOffModules.has('shops') ? (
          <ShopsHub
            workspace={activeWorkspace}
            initialTab={pendingOpsTab ?? undefined}
            onClearInitialTab={handleClearPendingOpsTab}
          />
        ) : activeLayoutView === 'STAYS' && !switchedOffModules.has('stays') ? (
          <StaysHub
            workspace={activeWorkspace}
            initialTab={pendingOpsTab ?? undefined}
            onClearInitialTab={handleClearPendingOpsTab}
          />
        ) : activeLayoutView === 'JOBS' && !switchedOffModules.has('jobs') ? (
          <JobsHub
            workspace={activeWorkspace}
            initialTab={pendingOpsTab ?? undefined}
            onClearInitialTab={handleClearPendingOpsTab}
          />
        ) : activeLayoutView === 'CLINICS' && !switchedOffModules.has('clinics') ? (
          <ClinicsHub
            workspace={activeWorkspace}
            initialTab={pendingOpsTab ?? undefined}
            onClearInitialTab={handleClearPendingOpsTab}
          />
        ) : activeLayoutView === 'PLANT' && !switchedOffModules.has('plant') ? (
          <PlantHub
            workspace={activeWorkspace}
            initialTab={pendingOpsTab ?? undefined}
            onClearInitialTab={handleClearPendingOpsTab}
          />
        ) : activeLayoutView === 'FITNESS' && !switchedOffModules.has('fitness') ? (
          <FitnessHub
            workspace={activeWorkspace}
            initialTab={pendingOpsTab ?? undefined}
            onClearInitialTab={handleClearPendingOpsTab}
          />
        ) : activeLayoutView === 'UPLOAD' && !switchedOffModules.has('upload') ? (
          isMobileViewport ? (
            <DesktopOnlyFallback title="Upload Hub" />
          ) : (
            <UploadHubLayout
              onManagePlan={openStoragePurchase}
              onOpenLinkHub={appMode === 'TEAM' && canPerform('integrations.view') && !switchedOffModules.has('linkHub') ? () => openPage('LINK') : undefined}
              onOpenChecklists={() => openPage('CHECKLISTS')}
              artifactFilter={uploadArtifactFilter}
            />
          )
        ) : activeLayoutView === 'FORMS' && !switchedOffModules.has('forms') && canPerform('forms.view') ? (
          isMobileViewport ? (
            <DesktopOnlyFallback title="Form Hub" />
          ) : (
            <FormsLayout
              onOpenLink={appMode === 'TEAM' && canPerform('integrations.view') && !switchedOffModules.has('linkHub') ? () => openPage('LINK') : undefined}
              onOpenExplore={() => openExploreCategory('forms')}
              onOpenWorkspaceSettings={canPerform('workspace.edit_settings') ? () => openWorkspaceSettingsSection('FORMS') : undefined}
            />
          )
        ) : activeLayoutView === 'MEETINGS' && !switchedOffModules.has('meetings') && canPerform('meetings.view') ? (
          <MeetingLayout
            initialMeetingId={pendingMeetingId}
            onOpenChat={appMode === 'TEAM' && canPerform('chat.view') ? openMeetingChat : undefined}
            onOpenDocs={openMeetingDocuments}
            onOpenLink={appMode === 'TEAM' && canPerform('integrations.view') && !switchedOffModules.has('linkHub') ? () => openPage('LINK') : undefined}
          />
        ) : activeLayoutView === 'AUTOMATE' && !switchedOffModules.has('automate') && canPerform('automation.view') ? (
          isMobileViewport ? (
            <DesktopOnlyFallback title="Automate Hub" />
          ) : (
            <AutomationHubLayout
              onOpenExplore={() => openExploreCategory('automations')}
              onOpenWorkspaceSettings={() => openWorkspaceSettingsSection('AUTOMATION')}
            />
          )
        ) : activeLayoutView === 'DESIGN' && !switchedOffModules.has('design') && canPerform('forms.view') ? (
          <DesignHubLayout
            onOpenExplore={() => openExploreCategory('designs')}
            onOpenUserSettings={openUserSettings}
            onOpenChecklistItem={(checklistItemId) => openChecklistDetail(checklistItemId)}
          />
        ) : activeLayoutView === 'OVERVIEW' ? (
          <WorkspaceOverviewPage
            onBack={() => setActiveLayoutView('DASHBOARD')}
            onNavigateToAnalysis={(tab) => openPage('ANALYSIS', `tab=${tab}`)}
            onOpenModule={(moduleKey) => {
              if (moduleKey === 'stock') openPage('STOCK')
              else if (moduleKey === 'budget') openPage('BUDGET')
              else if (moduleKey === 'teaching' || moduleKey === 'training') openPage('TEACHING')
            }}
          />
        ) : activeLayoutView === 'YOUR_PAGE' ? (
          <YourPage
            key={pendingCharacterHighlight ? `character-${pendingCharacterHighlight.kind}-${pendingCharacterHighlight.cosmeticId}` : 'details'}
            onBack={() => setActiveLayoutView('DASHBOARD')}
            initialTab={pendingCharacterHighlight ? 'CHARACTER' : undefined}
            highlightTarget={pendingCharacterHighlight}
            onHighlightConsumed={() => setPendingCharacterHighlight(null)}
            onOpenUserSettings={openUserSettings}
            hideFunPack={switchedOffModules.has('funPack')}
          />
        ) : activeLayoutView === 'PURCHASE' ? (
          <PurchasePage
            key={purchaseTab}
            onBack={() => setActiveLayoutView('DASHBOARD')}
            initialTab={purchaseTab}
            walletBalance={displayedGlobalPersonalCredits}
          />
        ) : activeLayoutView === 'CHAT' && appMode === 'TEAM' ? (
          isMobileViewport ? (
            <DesktopOnlyFallback title="Team Chat" />
          ) : (
            <ChatPage
              platform="desktop"
              onElementMentionClick={openElementMention}
              onSendChatsToAi={({ attachments, target }: { attachments: AiChatAttachment[]; target: AiChatHandoffTarget }) => {
                setAiMagicHandoff({
                  id: Date.now(),
                  attachments: attachments.filter((attachment) => attachment.kind === 'chat') as AiChatHandoffRequest['attachments'],
                  ...(target.kind === 'new' ? { mode: target.mode } : { targetSessionId: target.sessionId }),
                })
                openPage('AI_MAGIC')
              }}
              onOpenAiMagic={() => openPage('AI_MAGIC')}
              onOpenAiMagicSession={(session) => {
                setAiMagicOpenSessionRequest({ session, nonce: Date.now() })
                openPage('AI_MAGIC')
              }}
            />
          )
        ) : activeLayoutView === 'CALENDAR' ? (
          <CalendarPage
            activeBoard={activeBoard}
            onOpenMeeting={(meetingId) => {
              setPendingMeetingId(meetingId)
              setActiveLayoutView('MEETINGS')
            }}
            onOpenElement={(elementId) => {
              const element = allTimelineElementById.get(elementId)
              setActiveLayoutView('DASHBOARD')
              if (!element) return
              setSelectedElementId(element.id)
              if (element.type === 'GOAL') {
                jumpToLevel('goal', { goalId: element.id })
              } else if (element.type === 'TASK' && element.parentId) {
                jumpToLevel('task', { goalId: element.parentId, taskId: element.id })
              } else if (element.type === 'SUBTASK' && element.parentId) {
                const parentTask = allTimelineElementById.get(element.parentId)
                if (parentTask?.parentId) {
                  jumpToLevel('subtask', {
                    goalId: parentTask.parentId,
                    taskId: parentTask.id,
                    subTaskId: element.id,
                  })
                }
              }
            }}
            onOpenChecklistItem={(_parentElementId, checklistItemId) => openChecklistDetail(checklistItemId)}
          />
        ) : activeLayoutView === 'AI_MAGIC' && canMutate ? (
          <AiGeneratorPanel
            isOpen
            activeBoard={activeBoard}
            handoffRequest={aiMagicHandoff}
            externalOpenSessionRequest={aiMagicOpenSessionRequest}
            onOpenAutomate={canPerform('automation.view')
              ? (workflowId) => requestRightRailNavigation(() => {
                  const nextPath = routeBase
                    ? toConsolePath(`${routeBase}/automate${workflowId ? `?workflow=${encodeURIComponent(workflowId)}` : ''}`)
                    : `/console${workflowId ? `?workflow=${encodeURIComponent(workflowId)}` : ''}`
                  if (`${window.location.pathname}${window.location.search}` !== nextPath) {
                    window.history.pushState(null, '', nextPath)
                  }
                  setActiveLayoutView('AUTOMATE')
                })
              : undefined}
            onGeneratedGoal={(goal, meta) => {
              // Move-to-Manage lands the user on Manage REVIEW by default.
              // For generate mode the Firestore write already happened inside
              // openPendingPreview; for edit mode proposals seed the draft
              // context (seedManageDraftProposedEdits) so Review and Board both
              // pick them up.
              setSelectedElementId(null)
              const nextBoard = meta?.targetBoard ?? activeBoard
              if (meta?.mode === 'edit') {
                const proposals = collectAiEditProposedFields(goal, meta.baseline ?? null, meta.aiChangedIds ?? null)
                if (proposals.length > 0) seedManageDraftProposedEdits(proposals)
              }

              // Determine target focus and marker IDs:
              let targetFocus: DetailedSelection | null = null
              const createdIds: string[] = []
              let editedIds: string[] = []

              if (meta?.mode === 'edit') {
                editedIds = meta.aiChangedIds ? [...meta.aiChangedIds] : []
                if (editedIds.length > 0) {
                  // Prefer subtask > task > goal among changed IDs
                  for (const id of editedIds) {
                    const sel = findSelectionForNodeId(id, 'subtask', [goal])
                    if (sel) {
                      targetFocus = sel
                      break
                    }
                  }
                  if (!targetFocus) {
                    for (const id of editedIds) {
                      const sel = findSelectionForNodeId(id, 'task', [goal])
                      if (sel) {
                        targetFocus = sel
                        break
                      }
                    }
                  }
                  if (!targetFocus) {
                    for (const id of editedIds) {
                      const sel = findSelectionForNodeId(id, undefined, [goal])
                      if (sel) {
                        targetFocus = sel
                        break
                      }
                    }
                  }
                }
                if (!targetFocus) targetFocus = { kind: 'goal', goalId: goal.id }
              } else {
                // Generate mode: target created task (or goal if only goal created)
                if (goal.tasks && goal.tasks.length > 0) {
                  targetFocus = { kind: 'task', goalId: goal.id, taskId: goal.tasks[0].id }
                  for (const task of goal.tasks) {
                    createdIds.push(task.id)
                    for (const sub of task.subTasks ?? []) {
                      createdIds.push(sub.id)
                    }
                  }
                } else {
                  targetFocus = { kind: 'goal', goalId: goal.id }
                  createdIds.push(goal.id)
                }
              }

              const targetElementId = selectionElementId(targetFocus) ?? goal.id
              setPendingBoardOpenId(goal.id)
              setManageActiveElementId(targetElementId)
              setManageActiveWasExpanded(true)
              setDetailedViewInitialFocus(targetFocus)
              setDetailedViewAiNewIds(createdIds.length > 0 ? createdIds : null)
              setDetailedViewAiChangedIds(editedIds.length > 0 ? editedIds : null)
              setDetailedViewLiveScope('ALL')
              setDetailedViewMode('workspace')
              setDetailedViewChrome('manage')
              setManageMode('review')
              setActiveLayoutView('MANAGE')
              // pushState after every state commit so route listeners don't
              // observe half-batched state and re-trigger any layout switch.
              const nextPath = buildManageRoute(routeBase, appMode, nextBoard, 'review')
              if (nextPath && `${window.location.pathname}${window.location.search}` !== nextPath) {
                window.history.pushState(null, '', nextPath)
              }
            }}
          />
        ) : activeLayoutView === 'MANAGE' ? (
          <ManageWorkspacePage
            mode={manageMode}
            topChrome={(
              <ManageTopChrome
                mode={manageMode}
                boardCrumb={renderBoardBreadcrumb({
                  board: activeBoard,
                  openChain: boardOpenChain,
                  onBoardChange: (board) => {
                    setManageMode('board')
                    handleManageBoardChange(board, 'board')
                  },
                  setOpenElementId: setBoardOpenElementId,
                  getChildElements: (parentId) => getChildElementsByParent(parentId, activeBoard, timelineElements),
                })}
                reviewHeading={(
                  <ReviewHeadingContent
                    chrome="manage"
                    goalsList={detailedViewWorkingGoals ?? (detailedViewWorkingGoal ? [detailedViewWorkingGoal] : [])}
                    isWorkspaceMode={true}
                  />
                )}
                boardActions={<ManageActionsBar />}
                onSwitchMode={() => attemptManageModeSwitch(manageMode === 'board' ? 'review' : 'board')}
              />
            )}
            boardSlot={(
              <BoardExplorer
                board={activeBoard}
                onBoardChange={(board) => {
                  setManageMode('board')
                  handleManageBoardChange(board, 'board')
                }}
                openElementId={boardOpenElementId}
                onOpenElementIdChange={setBoardOpenElementId}
                onDashboardMoveComplete={openDashboardMoveManage}
                onOpenChecklistItem={(item) => openChecklistDetail(item.id)}
                pendingOpenElementId={pendingBoardOpenId}
                onPendingOpenConsumed={() => setPendingBoardOpenId(null)}
                pendingSelectOnlyElementId={pendingBoardSelectOnlyId}
                onPendingSelectOnlyConsumed={() => setPendingBoardSelectOnlyId(null)}
                onActiveChange={handleBoardActiveChange}
                manageMode={manageMode}
              />
            )}
            reviewSlot={(
              <DetailedViewScreen
                isOpen
                mode={detailedViewMode ?? 'workspace'}
                chrome={detailedViewChrome ?? 'manage'}
                activeBoard={detailedViewTargetBoard ?? activeBoard}
                appMode={appMode}
                workingGoal={detailedViewWorkingGoal}
                setWorkingGoal={setDetailedViewWorkingGoal}
                baselineGoal={detailedViewBaseline}
                workingGoals={detailedViewWorkingGoals}
                setWorkingGoals={setDetailedViewWorkingGoals}
                baselineGoals={detailedViewBaselineGoals}
                initialFocus={detailedViewInitialFocus}
                initialValidationIssues={detailedViewInitialIssues}
                aiNote={detailedViewAiNote}
                aiNewIds={detailedViewAiNewIds}
                aiChangedIds={detailedViewAiChangedIds}
                aiConfidence={detailedViewAiConfidence}
                aiOperationalProposal={detailedViewOperationalProposal}
                onSelectionChange={handleReviewSelectionChange}
                manageMode={manageMode}
                liveGoals={detailedViewLiveGoals}
                onRebaseToLive={rebaseDetailedViewToLive}
              />
            )}
          />
        ) : activeLayoutView === 'RAID' && appMode === 'TEAM' ? (
          <section className="featurePage railPageCard">
            <TeamRaidDashboard />
          </section>
        ) : activeLayoutView === 'FOR_YOU' && appMode === 'TEAM' ? (
          <ForYouPage onOpenRaid={!switchedOffModules.has('funPack') ? () => openPage('RAID') : undefined} />
        ) : activeLayoutView === 'FOCUS' ? (
          <FocusExperiencePage
            onOpenUserSettings={openUserSettings}
          />
        ) : activeLayoutView === 'CHECKLISTS' ? (
          <section className="featurePage railPageCard">
            <ChecklistsShell
              workspaceId={activeWorkspaceId ?? null}
              activeCanvasItem={activeCanvasItem}
              onImport={() => openExploreCategory('checklists')}
              onOpenExplore={() => openExploreCategory('checklists')}
              onOpenWorkspaceSettings={() => openWorkspaceSettingsSection('CHECKLISTS')}
            />
          </section>
        ) : activeLayoutView === 'ANALYSIS' && !switchedOffModules.has('analysis') && activeWorkspace && appMode === 'TEAM' && (canPerform('workspace.view_analysis') || canPerform('readonly.view_analysis')) ? (
          <AnalysisHub
            workspace={activeWorkspace}
            role={activeRole}
            elements={timelineElements}
            canViewFullAnalysis={canPerform('workspace.view_analysis')}
            onNavigate={navigateAnalysis}
          />
        ) : activeLayoutView === 'REPORTS' && !switchedOffModules.has('reports') && activeWorkspace && canPerform('workspace.view_reports') && appMode === 'TEAM' ? (
          <ReportCenter workspace={activeWorkspace} role={activeRole} onNavigate={navigateReports} />
        ) : activeLayoutView === 'SUMMARY' && activeWorkspace && roleCanPerform(activeRole, 'EDITOR') && auditReportId ? (
          <AuditReportView workspace={activeWorkspace} reportId={auditReportId} onBack={() => { setAuditReportId(null); window.location.hash = ''; openPage('SUMMARY') }} />
        ) : activeLayoutView === 'SUMMARY' && activeWorkspace && roleCanPerform(activeRole, 'EDITOR') ? (
          <AdminDashboard
            workspace={activeWorkspace}
            onBack={() => openPage('DASHBOARD')}
            standaloneAuditMode="SUMMARY"
            isMobile={isMobileViewport}
            onOpenAuditLedger={() => openPage('SUMMARY')}
            onOpenWorkspaceSettings={() => openWorkspaceSettingsSection('LIFECYCLE')}
          />
        ) : restrictedWorkspaceRoute ? (
          <section className="restrictedWorkspacePage">
          <p className="dropdownLabel">Workspace access</p>
          <h1>{restrictedWorkspaceRoute.name}</h1>
          <p>/{restrictedWorkspaceRoute.slug}</p>
          {restrictedWorkspaceRoute.isPublic ? (
            <p>This public workspace has a limited preview, but task and checklist details require membership.</p>
          ) : (
            <p>This workspace is private. Request access to join the group.</p>
          )}
          <div>
            <Button variant="primary" size="md" onClick={() => void requestRestrictedWorkspaceAccess()}>
              Send request to join group
            </Button>
            <Button variant="tertiary" size="md" onClick={() => setActiveLayoutView('EXPLORE')}>
              Open Explore Hub
            </Button>
          </div>
        </section>
      ) : activeLayoutView === 'WORKSPACE_SETTINGS' && activeWorkspace && canOpenAdminSettings ? (
        <AdminDashboard
          workspace={activeWorkspace}
          onBack={() => setActiveLayoutView('DASHBOARD')}
          initialIntent={workspaceSettingsIntent}
          initialSection={workspaceSettingsInitialSection}
          onConsumeIntent={consumeWorkspaceSettingsIntent}
          isMobile={isMobileViewport}
          onOpenAuditLedger={() => openPage('SUMMARY')}
        />
      ) : activeLayoutView === 'WORKSPACE_SETTINGS' && activeWorkspace && user ? (
        <MemberDashboard
          workspace={activeWorkspace}
          user={user}
          onBack={() => setActiveLayoutView('DASHBOARD')}
        />
      ) : (
        <>
          {routeNotFoundPath && (
            <section className="routeNotFoundBanner">
              <strong>Route not found</strong>
              <span>{routeNotFoundPath}</span>
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => {
                  setRouteNotFoundPath(null)
                  goToDashboard()
                  if (routeBase) window.history.pushState(null, '', toConsolePath(`${routeBase}/${BOARD_CODE_BY_KEY[activeBoard]}`))
                }}
              >
                Go to nearest workspace path
              </Button>
            </section>
          )}
          {activeLayoutView === 'DASHBOARD' && appMode === 'TEAM' ? (
            // WHY: Defense-in-depth: web inboxes must never mount inside the helper wrapper bundle.
            getWrapperRole() === 'helper' ? (
              (() => {
                console.warn('[App] PendingInviteInbox mount suppressed in helper wrapper.')
                return null
              })()
            ) : (
              <div className="mx-auto mt-4 w-[min(1120px,calc(100%-2rem))]">
                <PendingInviteInbox roleHint="MEMBER" compact hideWhenEmpty />
              </div>
            )
          ) : null}
          {shouldMountDashboardContainer({ hasTimelineContent, isAccessRestricted }) ? (
            <DashboardContainer
              activeBoard={activeBoard}
              summary={{
                goalCount: timelineGoalElements.length,
                totalDays: timelineStartDate && timelineEndDate
                  ? Math.round(getDurationInDays(timelineStartDate.getTime(), timelineEndDate.getTime()))
                  : totalDays,
                rangeLabel: timelineStartDate && timelineEndDate
                  ? formatRange({ startDate: timelineStartDate, endDate: timelineEndDate })
                  : bounds ? formatRange(bounds) : 'No bounds',
              }}
              pendingView={pendingDashboardView}
              onPendingViewConsumed={() => setPendingDashboardView(null)}
              onCurrentViewChange={setActiveDashboardView}
              onToggleWatch={watchTargetItem ? () => void toggleWatchActiveItem() : undefined}
              isWatching={isWatchingActiveItem}
              isWatchAvailable={Boolean(watchTargetItem)}
            />
          ) : (
            <DashboardCanvas goals={[]} />
          )}
        </>
      )}
      </Suspense>


      {activeWorkspace && (
        <WorkspaceWelcome
          workspace={activeWorkspace}
          isAdmin={roleCanPerform(activeRole, 'ADMIN')}
          onLandOnDefaultView={(defaultView) => {
            setActiveLayoutView('DASHBOARD')
            setActiveBoard('DASHBOARD')
            goToDashboard()
            if (defaultView) {
              setPendingDashboardView(DASHBOARD_VIEW_MAP[defaultView] ?? 'Timeline')
            }
          }}
          onOpenSettingsSection={(section) => {
            openWorkspaceSettingsSection(section)
          }}
          onOpenInvites={() => {
            openPage('INVITES')
          }}
          onOpenHelperInvite={openHelperInvite}
        />
      )}
      </div>
    </main>
  )
}

export default function App() {
  return (
    <XRayProvider>
      <OrbitSkinProvider>
        <OrbitProvider>
          <OrbitGestureLayer />
          <AppContent />
        </OrbitProvider>
      </OrbitSkinProvider>
    </XRayProvider>
  )
}

